/* app/content.jsx - Pages, Categories, Tags (per-site), wired to the live backend.
   Pages → /api/wp/pages (mirrors Posts); Categories/Tags → /api/sites/:siteId/{type}
   (list/create/edit/delete). Editing renames in place via PUT, so the term keeps
   its id and the posts filed under it stay filed under it; the backend tries the
   wpam plugin first and falls back to core WP REST on older plugin builds. */
import { api, apiList } from '../lib/api.js';
import { plainTitle } from '../lib/wp-text.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const fullUrlOf = (site) => { const u = (site && (site.urlFull || site.url)) || ""; return u.startsWith("http") ? u : (u ? "https://" + u : ""); };
  const fmtDate = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return String(iso); } };
  // `updated` is the MODIFIED date, which is what the column is labelled; it
  // used to be the publish date. `author` is a name filled in from the site's
  // user list (a page carries an author id), so it starts empty here.
  const mapPage = (p) => ({
    // `title.rendered` is HTML: decoded, or `&#8217;` shows as itself.
    id: p.id, title: plainTitle(p.title) || "(no title)", slug: p.slug ? "/" + p.slug : "",
    author: "", authorId: p.author != null ? p.author : null,
    cats: [], tags: [],   // pages are not filed under categories or tags
    seo: null, words: null,
    status: p.status === "publish" ? "published" : p.status === "pending" ? "review" : p.status === "trash" ? "trash" : "draft",
    updated: fmtDate(p.modified || p.date), modifiedRaw: p.modified || p.date || "",
    dateRaw: p.date || "", link: p.link || "", _raw: p,
  });

  /* Categories nest, so both the table and the parent picker need them in tree
     order: each child directly under its parent, one indent per level. A term
     whose parent is not in the list is treated as a root rather than dropped,
     because a screen that claims to list every category has to list every
     category even when the shape is broken. */
  function treeOrder(rows) {
    const ids = new Set(rows.map(r => r.id));
    const kids = {};
    for (const r of rows) {
      const p = ids.has(r.parent) ? r.parent : 0;
      (kids[p] = kids[p] || []).push(r);
    }
    const out = [];
    const walk = (pid, depth) => {
      for (const r of (kids[pid] || [])) {
        if (out.length > rows.length) return;   // a cycle cannot outrun this
        out.push({ ...r, depth });
        walk(r.id, depth + 1);
      }
    };
    walk(0, 0);
    // Anything a cycle left unvisited is appended flat, never hidden.
    if (out.length < rows.length) {
      const seen = new Set(out.map(r => r.id));
      for (const r of rows) if (!seen.has(r.id)) out.push({ ...r, depth: 0 });
    }
    return out;
  }
  /* The ids a term may NOT be filed under: itself, and everything beneath it.
     WordPress would refuse the write, and an option that cannot be chosen has
     no business being in the list. */
  function descendantIds(rows, id) {
    const out = new Set([id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const r of rows) if (!out.has(r.id) && out.has(r.parent)) { out.add(r.id); grew = true; }
    }
    return out;
  }
  const indent = (depth) => "\u00a0\u00a0\u00a0".repeat(Math.max(0, depth || 0));

  function statusBadge(status) {
    const { Badge } = DS();
    if (status === "published") return <Badge tone="success" dot>Published</Badge>;
    if (status === "review") return <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />In review</span>;
    return <span className="hds-badge hds-badge--draft"><span className="hds-badge__dot" />Draft</span>;
  }
  function seoChip(score) {
    const cls = score >= 75 ? "hds-badge--success" : score >= 50 ? "hds-badge--warning" : "hds-badge--danger";
    return <span className={"hds-badge " + cls} title="SEO score">{score}</span>;
  }

  /* ---- Pages: full Posts-style list, wired to /api/wp/pages ----
     The same two pieces of state Posts takes, for the same reason
     (docs/MODULE-SCOPE.md 1.1): `site` is the ANCHOR CONNECTION and `sectionId`
     is the FILTER, null at the All position. At one section this screen is what
     it has always been; at All it enumerates every CMS page of the website with
     the Section column and the per row verb.

     AND A PIECE WITH NO CMS LISTS THE PAGES IT SERVES, READ ONLY, which is
     MODULE-SCOPE's own Pages row ("a page with no CMS behind it still appears,
     read only", 3.5) and the owner's reported defect: "pages are 0 while we
     have 70 on the main site". Page Inventory listed all 70 and this screen
     said none, because it only ever asked a CMS. Zero was the answer to a
     question nobody asked: the front end has 70 pages, they just cannot be
     edited here, and reporting that as the same zero an empty blog reports
     makes the two indistinguishable.

     So the rows arrive, with the handover verb, through the SAME endpoint the
     All position uses, which reads them through Page Inventory's own filter.
     The two screens still mean different things (this one is the CMS lens plus
     what a CMS-less piece serves; Page Inventory is the served lens over the
     whole website) and they no longer contradict each other. */
  function PagesScreen({ site, sectionId, sections, onEdit }) {
    const { Button, IconButton, EmptyState } = DS();
    const fullUrl = fullUrlOf(site);
    const [tab, setTab] = useState("all");
    const [selected, setSelected] = useState([]);
    const [lists, setLists] = useState({ publish: [], draft: [], trash: [] });
    const [loading, setLoading] = useState(true);
    const [truncated, setTruncated] = useState(false);
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [menu, setMenu] = useState(null);        // { row, rect }: open ⋯ menu
    const [confirm, setConfirm] = useState(null);  // { kind, row }: pending action
    const [sort, setSort] = useState({ key: "updated", dir: "desc" });
    const [filters, setFilters] = useState({ ...window.MlScreens.EMPTY_FILTERS });
    const [filterAt, setFilterAt] = useState(null);
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const atAll = sectionId == null;
    /* A piece with no CMS has no `/wp/pages` to read, so it is answered by the
       property endpoint with its own section named: the same rows, the same
       read, `scope.kind` of `section`. That is why this is a UNION read rather
       than an All read, and why a CMS section is untouched. */
    const noCms = !!(site && site.source === "crawl");
    const union = atAll || noCms;
    const [reports, setReports] = useState({ publish: null, draft: null, trash: null });
    // Pages have no categories or tags, so the panel offers author and dates.
    const opts = window.MlScreens.useFilterOptions(fullUrl, siteId);
    const nFilters = window.MlScreens.countFilters(filters);
    const withSection = window.MlScreens.withSection;

    const load = useCallback(async () => {
      if (union) {
        if (siteId == null) { setLists({ publish: [], draft: [], trash: [] }); setReports({ publish: null, draft: null, trash: null }); setTruncated(false); setLoading(false); return; }
        setLoading(true);
        // One read per status, as per connection, so a tab means the same thing
        // at both positions and one status refusing does not empty the others.
        const blank = (e) => ({ items: [], sections: [], answered: null, sections_why: "", error: (e && e.message) || "could not be read" });
        try {
          const sec = atAll ? "" : `&section_id=${sectionId}`;
          const q = `/wp/content-property?site_id=${siteId}&type=pages${sec}`;
          const [publish, draft, trash] = await Promise.all([
            api(`${q}&status=publish`).catch(blank),
            api(`${q}&status=draft`).catch(blank),
            api(`${q}&status=trash`).catch(blank),
          ]);
          const one = (r) => ((r && r.items) || []).map(x => withSection(mapPage(x), x));
          setLists({ publish: one(publish), draft: one(draft), trash: one(trash) });
          setReports({ publish, draft, trash });
          setTruncated([publish, draft, trash].some(r => ((r && r.sections) || []).some(x => x.capped)));
        } catch { /* */ } finally { setLoading(false); }
        return;
      }
      if (!fullUrl) { setLists({ publish: [], draft: [], trash: [] }); setTruncated(false); setLoading(false); return; }
      setLoading(true);
      const enc = encodeURIComponent(fullUrl);
      // Pages through WordPress (see apiList) so the table isn't capped at one slice.
      const empty = { items: [], truncated: false };
      try {
        const [publish, draft, trash] = await Promise.all([
          apiList(`/wp/pages?siteUrl=${enc}&status=publish`).catch(() => empty),
          apiList(`/wp/pages?siteUrl=${enc}&status=draft`).catch(() => empty),
          apiList(`/wp/pages?siteUrl=${enc}&status=trash`).catch(() => empty),
        ]);
        // Through the same shape the union uses, with no section on it, so one
        // table reads one kind of row at both positions.
        const own = (x) => withSection(mapPage(x), {});
        setLists({ publish: publish.items.map(own), draft: draft.items.map(own), trash: trash.items.map(own) });
        setReports({ publish: null, draft: null, trash: null });
        setTruncated(publish.truncated || draft.truncated || trash.truncated);
      } catch { /* */ } finally { setLoading(false); }
    }, [union, atAll, sectionId, siteId, fullUrl]);
    useEffect(() => { setSelected([]); load(); }, [load]);

    const counts = { all: lists.publish.length + lists.draft.length, published: lists.publish.length, draft: lists.draft.length, trash: lists.trash.length };
    const base = tab === "published" ? lists.publish : tab === "draft" ? lists.draft : tab === "trash" ? lists.trash : [...lists.publish, ...lists.draft];
    const term = search.trim().toLowerCase();
    // Name the author, then search, then filter, then sort. `sort` owns the
    // order now and starts on the same newest-first the tab used to force.
    const authorById = {};
    for (const a of opts.authors) authorById[String(a.id)] = a.name;
    const named = base.map(p => (p.authorId != null && authorById[String(p.authorId)] ? { ...p, author: authorById[String(p.authorId)] } : p));
    const searched = term ? named.filter(p => (p.title || "").toLowerCase().includes(term)) : named;
    const filtered = nFilters ? searched.filter(p => window.MlScreens.passesFilters(p, filters)) : searched;
    const rows = window.MlScreens.sortRows(filtered, sort);
    const onSort = (key) => setSort(s2 => window.MlScreens.nextSort(s2, key));
    // The report of the read this tab is showing, and EVERY read for the
    // banner: a piece that answered for published pages and refused the trash
    // has to be named whichever tab is open.
    const tabReport = union ? (reports[(window.MlScreens.TAB_STATUSES[tab] || ["publish"])[0]] || null) : null;
    const reads = union
      ? ["publish", "draft", "trash"].map(k => ({ label: window.MlScreens.STATUS_LABEL[k], report: reports[k] })).filter(x => x.report)
      : [];
    /* NO TOTAL AT ALL (MODULE-SCOPE 1.1): a tab count is a sum over pieces with
       different CMSs, so at All the tabs carry no number and the per piece
       counts live in the report panel. At a section they are what they were. */
    /* The tab counts go only at the ALL position, where they would be a sum
       over pieces with different CMSs. A crawl-only section is one piece, so
       its counts add up and stay. */
    const tabs = [
      { id: "all", label: "All", count: atAll ? null : counts.all },
      { id: "published", label: "Published", count: atAll ? null : counts.published },
      { id: "draft", label: "Drafts", count: atAll ? null : counts.draft },
      { id: "trash", label: "Trash", count: atAll ? null : counts.trash },
    ];
    // Keyed on rowKey and never on the page id: two sections hold colliding
    // ids, so a selection held as ids ticks another piece's row.
    const allChecked = rows.length > 0 && rows.every(p => selected.includes(p.rowKey));
    const toggleAll = () => setSelected(allChecked ? [] : rows.map(p => p.rowKey));
    const toggleOne = (key) => setSelected(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key]);
    // The address a write goes to is the ROW's. The anchor's is the fallback
    // only where there is no union, which is every row at a section.
    const urlOf = (row) => row.siteUrl || fullUrl;
    const heldBack = rows.filter(p => selected.includes(p.rowKey) && !p.canEdit).length;
    const bulk = async (fn) => {
      const picked = rows.filter(p => selected.includes(p.rowKey) && p.canEdit && urlOf(p));
      if (!picked.length) return;
      setBusy(true);
      try { for (const row of picked) await fn(row); setSelected([]); await load(); } catch { /* */ } finally { setBusy(false); }
    };
    const bulkPublish = () => bulk(row => api(`/wp/pages/${row.id}`, { method: "PUT", body: JSON.stringify({ siteUrl: urlOf(row), status: "publish" }) }));
    const bulkTrash = () => bulk(row => api(`/wp/pages/${row.id}?siteUrl=${encodeURIComponent(urlOf(row))}&force=false`, { method: "DELETE" }));

    // Row ⋯ menu: shared with Posts (see screens.jsx RowMenu).
    const pickRowAction = (kind, row) => {
      if (kind === "edit") { onEdit && onEdit(row, row.sectionId); return; }
      if (kind === "view") { row.link && window.open(row.link, "_blank", "noopener"); return; }
      setConfirm({ kind, row });
    };
    const runRowAction = async () => {
      if (!confirm) return;
      const { kind, row } = confirm;
      setConfirm(null); setBusy(true); setErr(null);
      const target = urlOf(row);
      const enc = encodeURIComponent(target);
      try {
        if (kind === "publish") await api(`/wp/pages/${row.id}`, { method: "PUT", body: JSON.stringify({ siteUrl: target, status: "publish" }) });
        if (kind === "trash")   await api(`/wp/pages/${row.id}?siteUrl=${enc}&force=false`, { method: "DELETE" });
        if (kind === "delete")  await api(`/wp/pages/${row.id}?siteUrl=${enc}&force=true`, { method: "DELETE" });
        await load();
      } catch (e) { setErr(e.message || "Action failed"); }
      finally { setBusy(false); }
    };

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">Pages
              <span className="ml-draftnotice"><MlIcon name="info" size={14} /> All saves go to Draft</span>
            </h1>
            <p className="ml-pagehead__sub">
              {atAll
                ? <>Every page of this website, each row naming the piece it belongs to. A piece with a CMS contributes what that CMS holds; a piece with none contributes the pages it serves, read only.</>
                : noCms
                  ? <>The <strong>{site.name}</strong> pages, read by crawling what it serves. There is no CMS behind this piece, so every row is read only and the deliverable is a brief for whoever builds it.</>
                  : <>Build and publish static pages on <strong>{site.name}</strong>. Publishing is always an explicit action.</>}
            </p>
          </div>
          <div className="ml-pagehead__actions">
            {/* A new page needs ONE CMS to be created in, and at All the
                switcher is naming no piece. */}
            <Button variant="primary" leadingIcon="plus" size="md" disabled={union}
              title={atAll
                ? "Pick the piece of the website to write in from the switcher first: a new page has to be created in one CMS."
                : (noCms ? "This piece of the website has no CMS behind it, so there is nothing here to create a page in and no button can change that. The deliverable is a brief for whoever builds it." : undefined)}
              onClick={() => onEdit && onEdit(null)}>New page</Button>
          </div>
        </div>

        {err && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "danger", title: "Action failed" }, err)}
          </div>
        )}

        {union && React.createElement(window.MlScreens.UnionReport, { subject: "page", report: tabReport, rows })}
        {union && React.createElement(window.MlScreens.BrokenSections, { reads, subject: "pages" })}

        <div className="hds-table-wrap">
          <div style={{ padding: "0 16px" }}>
            <div className="hds-tabs">
              {tabs.map(t => (
                <button key={t.id} className={["hds-tab", tab === t.id ? "hds-tab--active" : ""].filter(Boolean).join(" ")} onClick={() => { setTab(t.id); setSelected([]); }}>
                  {t.label}{t.count == null ? null : <span className="hds-tab__count">{t.count}</span>}
                </button>
              ))}
            </div>
          </div>

          {selected.length === 0 ? (
            <div className="ml-table-toolbar">
              <div className="ml-table-search">
                <div className="hds-input-group">
                  <span className="hds-input-group__icon"><MlIcon name="search" size={16} /></span>
                  <input className="hds-input" placeholder="Search pages" aria-label="Search pages" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
              </div>
              <Button variant="secondary" size="sm" leadingIcon="filter"
                onClick={e => setFilterAt(filterAt ? null : e.currentTarget.getBoundingClientRect())}>
                Filters{nFilters ? ` (${nFilters})` : ""}
              </Button>
              {nFilters > 0 && (
                <button type="button" className="hds-btn hds-btn--sm hds-btn--ghost" onClick={() => setFilters({ ...window.MlScreens.EMPTY_FILTERS })}>Clear</button>
              )}
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                {rows.length} {rows.length === 1 ? "page" : "pages"}{nFilters || term ? ` of ${base.length}` : ""}
                {atAll ? " on screen" : ""}
              </span>
            </div>
          ) : (
            <div className="ml-table-toolbar ml-table-toolbar--bulk">
              <strong style={{ fontSize: "var(--text-sm)" }}>{selected.length} selected</strong>
              {heldBack > 0 && (
                <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                  {heldBack} of them cannot be written and are held back
                </span>
              )}
              <div style={{ flex: 1 }} />
              <Button variant="secondary" size="sm" leadingIcon="check-circle" disabled={busy} onClick={bulkPublish}>Publish</Button>
              <Button variant="secondary" size="sm" leadingIcon="trash" disabled={busy} onClick={bulkTrash}>Move to trash</Button>
            </div>
          )}

          {loading ? (
            <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Loading pages…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: "64px 24px" }}>
              {React.createElement(EmptyState, tab === "trash"
                ? { icon: "trash", title: "Trash is empty", description: "Pages you move to trash will appear here for 30 days before they're permanently deleted." }
                : term || nFilters ? { icon: "search", title: "No matches", description: nFilters && term ? "No pages match your search and these filters." : nFilters ? "No pages match these filters." : "No pages match your search." }
                  : noCms
                    ? { icon: "file", title: "No pages served here yet", description: "This piece of the website has no CMS behind it, so its pages are read by crawling what it serves, and the last crawl found none under this tab. Run the crawl from Page Inventory to look again." }
                    : atAll
                    ? { icon: "file", title: "No pages on this website yet", description: "No piece of this website contributed a page under this tab. The pieces that answered are listed above, with what each one contributed." }
                    : { icon: "file", title: "No pages yet", description: "Pages you create will appear here. New pages always start as drafts." })}
            </div>
          ) : (
            <table className="hds-table">
              <thead>
                <tr>
                  <th className="ml-th-check"><label className="hds-check"><input type="checkbox" checked={allChecked} onChange={toggleAll} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></th>
                  {React.createElement(window.MlScreens.SortTh, { id: "title", label: "Title", sort, onSort })}
                  {/* The Section column, which a section filter removes and
                      nothing else (MODULE-SCOPE 1.1). */}
                  {atAll && React.createElement(window.MlScreens.SortTh, { id: "section", label: "Section", sort, onSort })}
                  {/* Both halves of the label, which MODULE-SCOPE 7 insists on:
                      "found by this crawl" and "served by this section" are
                      different facts, and a row that hides the difference puts
                      the conflation back after the data refused it. */}
                  {union && React.createElement(window.MlScreens.SortTh, { id: "readFrom", label: "Read from", sort, onSort })}
                  {React.createElement(window.MlScreens.SortTh, { id: "author", label: "Author", sort, onSort })}
                  {/* SEO and Words are not in the list response, so there is
                      nothing to order them by. */}
                  <th>SEO</th><th>Words</th>
                  {React.createElement(window.MlScreens.SortTh, { id: "status", label: "Status", sort, onSort })}
                  {React.createElement(window.MlScreens.SortTh, { id: "updated", label: "Updated", sort, onSort })}
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.rowKey} className={p.status !== "published" ? "ml-row--draft" : ""} style={{ cursor: p.canEdit ? "pointer" : "default" }} onClick={() => p.canEdit && onEdit && onEdit(p, p.sectionId)}>
                    <td className="ml-checkcell" onClick={e => e.stopPropagation()}><label className="hds-check"><input type="checkbox" checked={selected.includes(p.rowKey)} onChange={() => toggleOne(p.rowKey)} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></td>
                    <td><div className="ml-rowtitle">{p.title}</div><div className="ml-rowsub">{p.slug}</div></td>
                    {atAll && React.createElement(window.MlScreens.SectionCell, { row: p })}
                    {union && (
                      <td className="hds-table__muted">
                        <span className="bm-kw-chip" title={p.listedWhy || ""}>{p.readFrom === "crawl" ? "the live page" : "the CMS"}</span>
                      </td>
                    )}
                    <td className="hds-table__muted">{p.author || "-"}</td>
                    <td>{p.seo == null ? <span className="hds-table__muted">-</span> : seoChip(p.seo)}</td>
                    <td className="hds-table__muted" style={{ fontVariantNumeric: "tabular-nums" }}>{p.words == null ? "-" : p.words.toLocaleString()}</td>
                    <td>{statusBadge(p.status)}</td>
                    <td className="hds-table__muted">{p.updated}</td>
                    {/* The verb is the ROW's (MODULE-SCOPE 1.2). */}
                    <td onClick={e => e.stopPropagation()}><div className="ml-rowactions"><IconButton icon="edit" label={p.canEdit ? "Edit" : "Read only"} size="sm" disabled={!p.canEdit} title={p.canEdit ? undefined : (p.verbWhy || "")} onClick={() => p.canEdit && onEdit && onEdit(p, p.sectionId)} /><IconButton icon="external-link" label="View" size="sm" onClick={() => p.link && window.open(p.link, "_blank", "noopener")} /><IconButton icon="more-horizontal" label="More" size="sm" onClick={(e) => setMenu({ row: p, rect: e.currentTarget.getBoundingClientRect() })} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab !== "trash" && (
            <div className="ml-pagination">
              <span>
                {atAll
                  ? <>Showing {rows.length} row(s) from {((tabReport && tabReport.sections) || []).length} piece(s). No total: the panel above says why.</>
                  : <>Showing {rows.length} of {counts.all}</>}
                {truncated && <> · <strong>only the first 1,000 loaded</strong>: narrow by status or search</>}
              </span>
              <div className="ml-pagination__pages">
                <Button variant="secondary" size="sm" disabled leadingIcon="chevron-left">Prev</Button>
                <Button variant="secondary" size="sm" trailingIcon="chevron-right">Next</Button>
              </div>
            </div>
          )}
        </div>

        {filterAt && React.createElement(window.MlScreens.FiltersPopover, {
          rect: filterAt, value: filters, onChange: setFilters, opts, showTerms: false, onClose: () => setFilterAt(null),
        })}
        {menu && React.createElement(window.MlScreens.RowMenu, {
          row: menu.row, rect: menu.rect, onClose: () => setMenu(null), onPick: pickRowAction,
        })}
        {confirm && React.createElement(window.MlBiModals.ConfirmDialog, {
          ...window.MlScreens.ROW_CONFIRM[confirm.kind](confirm.row.title, "page"),
          onClose: () => setConfirm(null),
          onConfirm: runRowAction,
        })}
      </div>
    );
  }

  /* ---- shared taxonomy table (Categories & Tags), wired to /api/sites/:siteId/:type ----
     THE ALL POSITION HERE CARRIES A DECLARED EXCEPTION, and this screen is the
     one place in the migration where the exception is visible. MODULE-SCOPE's
     table puts Categories and Tags at "section, with a declared exception", and
     6.1 is UNSETTLED: a term pool belongs to a WordPress INSTALL, which is
     neither the property nor the section, and the live property has two
     sections sharing one. NOTHING HERE SETTLES IT.

     What it does instead is enumerate honestly. A term of a pool two sections
     are DECLARED to share is ONE row naming both, because that is what one
     `wp_terms` table is and listing it twice would send somebody to create a
     category that already exists. Two sections not declared to share an install
     keep their terms apart even when the names match, because merging on a
     resemblance is the inference PROPERTY-MODEL 3.7b forbids. Where the
     declaration is missing the screen says the answer is not known, and the
     overlap it observed is stated as an observation rather than acted on.

     The rule itself is the server's (backend/lib/term-pool.js) and NOTHING IS
     RE-DERIVED HERE: which pool a term is in, which sections it is listed
     under, where an edit is sent and the sentence explaining each of those all
     come off the row. A second opinion in the client is how one screen comes to
     disagree with another about what a website is. */
  function TaxonomyScreen({ site, sectionId, sections, type, title, sub, newLabel, nameHeader }) {
    const { Button, IconButton } = DS();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const fullUrl = fullUrlOf(site);
    const enc = encodeURIComponent(fullUrl);
    const atAll = sectionId == null;
    const [rows, setRows] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
      if (atAll) {
        if (siteId == null) { setRows([]); setReport(null); setLoading(false); return; }
        setLoading(true);
        try {
          const r = await api(`/wp/terms-property?site_id=${siteId}&type=${type}`);
          setRows(((r && r.items) || []).map(t => ({
            id: t.id, name: t.name || "", slug: t.slug || "",
            // A term id is unique inside ONE pool and means nothing across two,
            // so a row's identity on screen is the pool plus the id. Keyed on
            // the id alone, two installs holding term 7 would share a React key
            // and one tick would select both.
            rowKey: `${t.install_key || "one"}#${t.id}`,
            // Null, not 0: a CMS that sent no count has not measured none, and
            // "0 posts" beside a term that has some is a figure nobody can
            // contradict on screen.
            count: t.count == null ? null : t.count,
            desc: t.description || "",
            parent: t.parent == null ? 0 : t.parent,
            // Every row names the section(s) it is listed under, the install
            // whose pool it lives in, and where an edit to it is sent.
            sectionsList: Array.isArray(t.sections) ? t.sections : [],
            installKey: t.install_key || null,
            poolShared: !!t.pool_shared,
            poolWhy: t.pool_why || null,
            editSectionId: t.edit_section_id == null ? null : t.edit_section_id,
            editSectionName: t.edit_section_name || "",
            editUrl: t.edit_url || "",
            verb: t.verb || "manage",
            verbWhy: t.verb_why || null,
            canEdit: t.can_edit !== false,
          })));
          setReport(r || null);
        } catch (e) {
          setRows([]);
          setReport({ sections: [], answered: null, sections_why: "", error: (e && e.message) || "could not be read" });
        } finally { setLoading(false); }
        return;
      }
      if (siteId == null || !fullUrl) { setRows([]); setLoading(false); return; }
      setLoading(true);
      try {
        const data = await api(`/sites/${siteId}/${type}?siteUrl=${enc}`);
        const arr = Array.isArray(data) ? data : (data && data.terms) || [];
        setRows(arr.map(t => ({
          id: t.id, name: t.name || "", slug: t.slug || "",
          rowKey: String(t.id),
          count: t.count != null ? t.count : (t.post_count || 0),
          desc: t.description || "",
          // Tags are flat; `parent` is 0 for them and the UI never offers it.
          parent: Number(t.parent) || 0,
          // At one section the row's section IS the screen's, so these are the
          // identity case rather than a union: one pool, one address, one verb.
          sectionsList: [], installKey: null, poolShared: false, poolWhy: null,
          editSectionId: siteId, editSectionName: (site && site.name) || "", editUrl: fullUrl,
          verb: "manage", verbWhy: null, canEdit: true,
        })));
        setReport(null);
      } catch { setRows([]); } finally { setLoading(false); }
    }, [atAll, siteId, type, enc, fullUrl]);
    useEffect(() => { setSelected([]); load(); }, [load]);

    const term = search.trim().toLowerCase();
    const nests = type === "categories";
    /* A TREE IS PER POOL, which is the one thing a union list of terms would
       get wrong by accident. A parent id means something only inside its own
       `wp_terms` pool, so nesting the whole union in one pass would file a
       category of install A under whatever term happens to hold that id in
       install B. Each pool is ordered on its own and the groups are
       concatenated in the order the pieces are listed. */
    const byPool = (list) => {
      const groups = new Map();
      for (const r of list) {
        const k = r.installKey || "one";
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
      }
      const out = [];
      for (const g of groups.values()) out.push(...treeOrder(g));
      return out;
    };
    // A filtered list cannot be a tree: the parent of a match may not match, so
    // search flattens rather than showing children under absent parents.
    const view = term
      ? rows.filter(r => r.name.toLowerCase().includes(term) || r.slug.toLowerCase().includes(term)).map(r => ({ ...r, depth: 0 }))
      : (nests ? byPool(rows) : rows.map(r => ({ ...r, depth: 0 })));
    // A parent can only be a term of the SAME pool as the row being edited.
    const parentChoices = nests ? byPool(rows) : [];
    const allChecked = view.length > 0 && view.every(r => selected.includes(r.rowKey));
    const toggleAll = () => setSelected(allChecked ? [] : view.map(r => r.rowKey));
    const toggleOne = (key) => setSelected(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key]);

    /* The same fields WordPress asks for on its own Add New screen: name, slug,
       parent (categories only, tags are flat) and description. The route and the
       plugin have always accepted all four. */
    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState("");
    const [createSlug, setCreateSlug] = useState("");
    const [createParent, setCreateParent] = useState("0");
    const [createDesc, setCreateDesc] = useState("");
    const [createBusy, setCreateBusy] = useState(false);
    const [createErr, setCreateErr] = useState("");
    const create = () => { setCreateName(""); setCreateSlug(""); setCreateParent("0"); setCreateDesc(""); setCreateErr(""); setCreateOpen(true); };
    const doCreate = async () => {
      if (siteId == null || atAll || !createName.trim()) return;
      setCreateBusy(true); setCreateErr("");
      // Each optional field is sent only when it was filled in, so WordPress
      // keeps generating the slug itself when the field was left alone.
      const body = { siteUrl: fullUrl, name: createName.trim() };
      if (createSlug.trim()) body.slug = createSlug.trim();
      if (createDesc.trim()) body.description = createDesc.trim();
      if (nests && Number(createParent) > 0) body.parent = Number(createParent);
      try {
        await api(`/sites/${siteId}/${type}`, { method: "POST", body: JSON.stringify(body) });
        setCreateOpen(false);
        await load();
      } catch (e) {
        // This used to fail in silence: a duplicate name closed nothing, said
        // nothing, and left the user pressing Create again.
        setCreateErr(e.message || "Could not create it. A term with that name or slug may already exist.");
      } finally { setCreateBusy(false); }
    };
    /* Editing renames the term in place rather than deleting and recreating it,
       which is the whole point: WordPress keeps the term_id, so every post
       already filed under it stays filed under it. */
    const [editRow, setEditRow] = useState(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editParent, setEditParent] = useState("0");
    const [editBusy, setEditBusy] = useState(false);
    const [editErr, setEditErr] = useState("");
    const openEdit = (row) => { setEditRow(row); setEditName(row.name || ""); setEditDesc(row.desc || ""); setEditParent(String(row.parent || 0)); setEditErr(""); };
    /* WHERE A WRITE GOES, and on this screen it is the most consequential
       lookup there is. A term id means something only inside its own pool, so a
       rename addressed to the wrong connection renames whatever term happens to
       hold that id in another website. The address and the connection id are
       both ON THE ROW, put there by the server, and neither falls back to the
       anchor's. */
    const target = (row) => ({
      id: row && row.editSectionId != null ? row.editSectionId : siteId,
      url: (row && row.editUrl) || fullUrl,
    });
    const doEdit = async () => {
      if (!editRow || !editName.trim()) return;
      const t = target(editRow);
      if (t.id == null || !t.url) { setEditErr("This term's piece of the website has no address stored, so it cannot be written."); return; }
      setEditBusy(true); setEditErr("");
      try {
        // Description is always sent, so clearing it actually clears it. The
        // backend and the plugin both treat absent and empty as different.
        const body = { siteUrl: t.url, name: editName.trim(), description: editDesc };
        // Re-filing a category under a different parent moves it and its
        // children with it; the term keeps its id, so posts stay filed here.
        if (nests) body.parent = Number(editParent) || 0;
        await api(`/sites/${t.id}/${type}/${editRow.id}`, { method: "PUT", body: JSON.stringify(body) });
        setEditRow(null);
        await load();
      } catch (e) { setEditErr(e.message || "Could not save the change."); }
      finally { setEditBusy(false); }
    };

    // Computed once per render rather than once per <option>, and over the
    // row's OWN pool: a descendant in another install is not a descendant.
    const editBlocked = (nests && editRow)
      ? descendantIds(rows.filter(r => (r.installKey || "one") === (editRow.installKey || "one")), editRow.id)
      : null;

    const delOne = async (row) => {
      const t = target(row);
      if (t.id == null || !t.url) return;
      await api(`/sites/${t.id}/${type}/${row.id}?siteUrl=${encodeURIComponent(t.url)}`, { method: "DELETE" });
    };
    const del = async (row) => { if (!row.canEdit) return; try { await delOne(row); await load(); } catch { /* */ } };
    const bulkDelete = async () => {
      const picked = view.filter(r => selected.includes(r.rowKey) && r.canEdit);
      if (!picked.length) return;
      setBusy(true);
      try { for (const row of picked) await delOne(row); setSelected([]); await load(); } catch { /* */ } finally { setBusy(false); }
    };

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">{title}</h1>
            <p className="ml-pagehead__sub">
              {atAll
                ? <>{sub} across every piece of this website, each row naming where it is listed.</>
                : <>{sub}{site ? " on " + site.name : ""}.</>}
            </p>
          </div>
          {/* A new term has to be created in ONE pool, and at All the switcher
              is naming no piece, so there is no pool to put it in. The button
              says to choose rather than picking an install for the customer:
              a term created in the wrong pool is invisible to the posts it was
              meant for and has to be deleted by hand. */}
          <div className="ml-pagehead__actions"><Button variant="primary" leadingIcon="plus" disabled={atAll}
            title={atAll ? "Pick the piece of the website from the switcher first: a new term is created in one WordPress term pool." : undefined}
            onClick={create}>{newLabel}</Button></div>
        </div>
        {atAll && React.createElement(window.MlScreens.UnionReport, {
          subject: type === "tags" ? "tag" : "category",
          report, rows: view,
          // A term of a shared pool is one row listed under several pieces.
          has: (r, id) => (r.sectionsList || []).some(x => x.section_id === id),
          verbs: { manage: "Create, rename, delete" },
        }, (
          /* The 6.1 half, said on screen because it is a live open question and
             not a footnote: which installs back this website, which of them two
             pieces share, and where the declaration is missing. */
          <>
            {report && report.pool_why ? <p className="bm-kw-note">{report.pool_why}</p> : null}
            {((report && report.installs) || []).filter(i => i.pool_shared).map(i => (
              <p className="bm-kw-note" key={i.install_key}>{i.why}</p>
            ))}
            {report && report.pool_unknown ? <p className="bm-kw-note bm-kw-note--bad">{report.pool_unknown_why}</p> : null}
            {((report && report.undeclared_overlap) || []).map((o, n) => (
              <p className="bm-kw-note bm-kw-note--bad" key={"ov" + n}>{o.why}</p>
            ))}
          </>
        ))}
        {atAll && React.createElement(window.MlScreens.BrokenSections, {
          reads: [{ label: type, report }], subject: title.toLowerCase(),
        })}
        {createOpen && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: newLabel, onClose: createBusy ? () => {} : () => setCreateOpen(false),
          foot: <><button className="bi-btn" onClick={() => setCreateOpen(false)} disabled={createBusy}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={doCreate} disabled={createBusy || !createName.trim()}>{createBusy ? "Creating…" : "Create"}</button></>,
        }, (
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8 }}>
            {React.createElement(DS().Field, { label: nameHeader }, React.createElement(DS().Input, {
              value: createName, autoFocus: true, placeholder: "Name",
              onChange: (e) => setCreateName(e.target.value),
              onKeyDown: (e) => { if (e.key === "Enter") doCreate(); },
            }))}
            <div style={{ marginTop: 12 }}>
              {React.createElement(DS().Field, { label: "Slug", hint: "Lowercase letters, numbers and hyphens. Leave empty and WordPress makes one from the name." }, React.createElement(DS().Input, {
                value: createSlug, placeholder: "auto from the name",
                onChange: (e) => setCreateSlug(e.target.value),
                onKeyDown: (e) => { if (e.key === "Enter") doCreate(); },
              }))}
            </div>
            {nests && (
              <div style={{ marginTop: 12 }}>
                {React.createElement(DS().Field, { label: "Parent category", hint: "Categories nest. Tags do not, which is why this field is only here." },
                  React.createElement(DS().Select, { value: createParent, onChange: (e) => setCreateParent(e.target.value) },
                    React.createElement("option", { value: "0" }, "None (top level)"),
                    parentChoices.map(c => React.createElement("option", { key: c.id, value: String(c.id) }, indent(c.depth) + c.name))
                  ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              {React.createElement(DS().Field, { label: "Description" }, React.createElement(DS().Textarea, {
                rows: 3, value: createDesc, placeholder: "Optional. Some themes show this on the archive page.",
                onChange: (e) => setCreateDesc(e.target.value),
              }))}
            </div>
            {createErr && <div style={{ marginTop: 10, fontSize: "var(--text-sm)", color: "var(--danger)" }}>{createErr}</div>}
          </div>
        ))}
        {editRow && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: `Edit ${nameHeader.toLowerCase()}`, onClose: editBusy ? () => {} : () => setEditRow(null),
          foot: <>
            <button className="bi-btn" onClick={() => setEditRow(null)} disabled={editBusy}>Cancel</button>
            <button className="bi-btn bi-btn--primary" onClick={doEdit} disabled={editBusy || !editName.trim()}>{editBusy ? "Saving…" : "Save"}</button>
          </>,
        }, (
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8 }}>
            {React.createElement(DS().Field, { label: nameHeader }, React.createElement(DS().Input, {
              value: editName, autoFocus: true,
              onChange: (e) => setEditName(e.target.value),
              onKeyDown: (e) => { if (e.key === "Enter") doEdit(); },
            }))}
            {nests && (
              <div style={{ marginTop: 12 }}>
                {React.createElement(DS().Field, { label: "Parent category" },
                  React.createElement(DS().Select, { value: editParent, onChange: (e) => setEditParent(e.target.value) },
                    React.createElement("option", { value: "0" }, "None (top level)"),
                    // Itself and its own children are left out: WordPress would
                    // refuse the write, so the option should not be offered.
                    // And so is every term of ANOTHER pool: a parent id only
                    // means something inside the install it came from, so at
                    // All the choices are this row's own install's.
                    parentChoices.filter(c => !editBlocked.has(c.id)
                        && (c.installKey || "one") === ((editRow && editRow.installKey) || "one"))
                      .map(c => React.createElement("option", { key: c.rowKey, value: String(c.id) }, indent(c.depth) + c.name))
                  ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              {React.createElement(DS().Field, { label: "Description" }, React.createElement(DS().Textarea, {
                rows: 3, value: editDesc, placeholder: "Optional",
                onChange: (e) => setEditDesc(e.target.value),
              }))}
            </div>
            <p style={{ margin: "10px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              The slug is left as it is, so existing links keep working. Posts filed here stay filed here.
              {atAll && editRow ? ` This change is written through ${editRow.editSectionName}.` : ""}
              {atAll && editRow && editRow.poolShared ? ` ${editRow.poolWhy || ""}` : ""}
            </p>
            {editErr && <div style={{ marginTop: 10, fontSize: "var(--text-sm)", color: "var(--danger)" }}>{editErr}</div>}
          </div>
        ))}
        <div className="hds-table-wrap">
          {selected.length === 0 ? (
            <div className="ml-table-toolbar">
              <div className="ml-table-search"><div className="hds-input-group"><span className="hds-input-group__icon"><MlIcon name="search" size={16} /></span><input className="hds-input" placeholder={"Search " + title.toLowerCase()} aria-label={"Search " + title} value={search} onChange={e => setSearch(e.target.value)} /></div></div>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                {view.length} {title.toLowerCase()}{atAll ? " on screen" : ""}
              </span>
            </div>
          ) : (
            <div className="ml-table-toolbar ml-table-toolbar--bulk"><strong style={{ fontSize: "var(--text-sm)" }}>{selected.length} selected</strong><div style={{ flex: 1 }} /><Button variant="secondary" size="sm" leadingIcon="trash" disabled={busy} onClick={bulkDelete}>Delete</Button></div>
          )}
          <table className="hds-table">
            <thead><tr><th className="ml-th-check"><label className="hds-check"><input type="checkbox" checked={allChecked} onChange={toggleAll} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></th><th>{nameHeader}</th>{atAll && <th>Listed under</th>}<th>Slug</th><th>Posts</th><th>Description</th><th style={{ width: 60 }}></th></tr></thead>
            <tbody>
              {view.map((c) => (
                <tr key={c.rowKey}>
                  <td className="ml-checkcell"><label className="hds-check"><input type="checkbox" checked={selected.includes(c.rowKey)} onChange={() => toggleOne(c.rowKey)} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></td>
                  <td className="hds-table__primary">
                    {c.depth ? <span style={{ display: "inline-block", width: c.depth * 18 }} /> : null}{c.name}
                  </td>
                  {/* EVERY SECTION THIS TERM IS LISTED UNDER, which is one chip
                      on an ordinary row and two on a term of a shared pool: one
                      term, listed once, named twice, which is what one
                      `wp_terms` table is. The sentence explaining it is the
                      server's. */}
                  {atAll && (
                    <td className="hds-table__muted">
                      {(c.sectionsList || []).map(x => (
                        <span className="bm-kw-chip" key={x.section_id} style={{ marginRight: 4 }}>{x.section_name}</span>
                      ))}
                      {c.poolShared && <span className="bm-kw-note" title={c.poolWhy || ""}> one shared pool</span>}
                    </td>
                  )}
                  <td className="hds-table__muted" style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>{c.slug}</td>
                  {/* Null is not zero: a CMS that reported no count has not
                      measured none, and a 0 here would read as a measurement. */}
                  <td>{c.count == null ? <span className="hds-table__muted" title="this CMS reported no count">-</span> : <span className="hds-badge hds-badge--neutral">{c.count}</span>}</td>
                  <td className="hds-table__muted">{c.desc}</td>
                  <td><div className="ml-rowactions"><IconButton icon="edit" label={c.canEdit ? `Edit ${c.name}` : "Read only"} size="sm" disabled={!c.canEdit} title={c.canEdit ? (atAll ? `Written through ${c.editSectionName}` : undefined) : (c.verbWhy || "")} onClick={() => c.canEdit && openEdit(c)} /><IconButton icon="trash" label="Delete" size="sm" disabled={!c.canEdit} onClick={() => del(c)} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading ? <div className="bi-subempty">Loading…</div> : view.length === 0 && <div className="bi-subempty">No {title.toLowerCase()} yet.</div>}
        </div>
      </div>
    );
  }

  /* WHOSE TERM IS THIS, reported under the list rather than inside it, because
     the answer is WORKED OUT and the list is read. One WordPress install can
     serve two sections (MULTILINGUAL-DIRECTORIES.md), and those sections share
     one `wp_terms` pool, so a term cannot say which of them it belongs to. Two
     different facts are shown and never folded into one: the directory a term
     was CREATED through, which the plugin records, and the sections whose posts
     actually USE it, which is derived on every read and labelled as derived.

     It sits here rather than behind its own nav entry for the house reason: a
     new kind of an existing thing belongs in that thing's screen, and two
     entries for one subject is how two screens come to disagree about what the
     website is. */
  function TermAttributionPanel({ site, type }) {
    const mod = typeof window !== "undefined" ? window.MlTermAttribution : null;
    if (!mod || !mod.TermAttribution || !site) return null;
    return <mod.TermAttribution site={site} type={type} />;
  }

  function CategoriesScreen({ site, sectionId, sections }) {
    return (<>
      <TaxonomyScreen site={site} sectionId={sectionId} sections={sections} type="categories" title="Categories" sub="Category taxonomy" newLabel="New category" nameHeader="Name" />
      <TermAttributionPanel site={site} type="categories" />
    </>);
  }
  function TagsScreen({ site, sectionId, sections }) {
    return (<>
      <TaxonomyScreen site={site} sectionId={sectionId} sections={sections} type="tags" title="Tags" sub="Tag taxonomy" newLabel="New tag" nameHeader="Tag" />
      <TermAttributionPanel site={site} type="tags" />
    </>);
  }

  /* A review that saw part of the taxonomy must not read as one that saw all of
     it, and a model that failed must not read as a model that found nothing.
     One definition, rendered both above the findings and in the scan window, so
     the two can never end up disagreeing about what the AI pass did. */
  function aiNotes(d) {
    const out = [];
    if (d.ai_error) out.push(<span key="err" style={{ color: "var(--danger)" }}>The AI pass failed ({d.ai_error}), so only exact matches are listed.</span>);
    if (d.ai_unreadable) out.push(<span key="bad" style={{ color: "var(--danger)" }}>The AI pass returned something unreadable, so only exact matches are listed.</span>);
    if (d.ai_not_reviewed > 0) out.push(<span key="cap">{d.ai_not_reviewed} terms were past the AI review limit and were not looked at by it.</span>);
    return out;
  }

  /* ---- a finding card opens the drawer, without swallowing what is inside it ----
     Same shape as the Bulk Meta Editor's guard, and for the same reasons: asking
     the event where it came from covers controls nobody has added yet, and
     covers markup this file does not own (anything a DS component renders inside
     itself), which cannot be given a stopPropagation from here.

     The selector is wider than what a finding card holds today on purpose. The
     card already holds buttons and it will hold links; the guard should be right
     on the day it does. */
  const FINDING_CONTROLS = 'a, button, input, textarea, select, label, [role="button"], [contenteditable="true"]';
  function fromFindingControl(e) {
    const t = e.target;
    /* The match must stop BELOW the card, and this is not a nicety: the card
       itself carries role="button" so it can be reached by keyboard, and
       `[role="button"]` is in the selector above. A plain closest() therefore
       walks up from anywhere inside, matches the CARD, and reports every click
       as coming from a control, so the drawer never opens at all. Comparing
       against currentTarget is what keeps the card out of its own guard. This
       shipped dead once on the Bulk Meta Editor and was only found by clicking
       one in a browser: the source reads correctly and source-level tests
       cannot see it. */
    if (t && typeof t.closest === "function") {
      const hit = t.closest(FINDING_CONTROLS);
      if (hit && hit !== e.currentTarget) return true;
    }
    // A click that ends a text selection is the end of a drag. Opening the
    // drawer there would snatch away the term somebody was part way through
    // copying, which on this screen is the likeliest thing to want to copy.
    const picked = typeof window.getSelection === "function" ? window.getSelection() : null;
    return !!(picked && String(picked).length > 0);
  }
  /* One definition of "this card opens the drawer", so a finding kind added
     later cannot arrive with the click but without the keyboard, or with
     neither guard. */
  const findingCardProps = (label, open, isOpen) => ({
    className: "hds-table-wrap bi-finding" + (isOpen ? " bi-finding--open" : ""),
    style: { padding: 12 },
    role: "button", tabIndex: 0, "aria-label": label,
    onClick: (e) => { if (!fromFindingControl(e)) open(); },
    onKeyDown: (e) => {
      // Only the card's own keys. A space pressed on the Dismiss button inside
      // it belongs to that button.
      if (e.target !== e.currentTarget) return;
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();   // Space on a focused card would scroll the page.
      open();
    },
  });

  /* Which member of a duplicate group survives.
     The default has to be the member the row's own button would have kept, so a
     user who opens the drawer and presses Apply gets exactly today's behaviour.
     fixesFor names a keeper only when the group is suggestions alone; when a
     live WordPress term is in the group that term IS the keeper, because every
     suggestion is rejected and the live one is what remains. Reading `keep` and
     falling back to the live member reproduces both cases without a second copy
     of the ranking living here, which would be free to drift. */
  function defaultKeepIndex(g) {
    const ms = (g && g.members) || [];
    if (g && g.keep && g.keep.id != null) {
      const i = ms.findIndex(m => m.source === "suggestion" && m.id === g.keep.id);
      if (i >= 0) return i;
    }
    const live = ms.findIndex(m => m.source === "wp");
    if (live >= 0) return live;
    return ms.length ? 0 : -1;
  }

  /* The finding drawer: the group's members, and which one you are keeping.
     No new endpoint is involved. Keeping a member means rejecting the OTHER
     suggestions, one at a time, through the same PATCH the row's button already
     uses, and live WordPress terms are never written to from here. */
  /* Where the open finding sits in the list the user can see, and whether there
     is anywhere to step.
     Pure and exported through window.MlContent so it can be tested: the rest of
     the drawer needs a browser, and this is the part with the edge cases.
     A key that is NOT in the list returns index -1 and no steps. That happens
     the moment a finding is applied or dismissed, because it leaves the visible
     list while the drawer stays open to report what it did. Guessing where it
     "would have been" could walk the user backwards into work already done. */
  function navPosition(keys, key, at) {
    const list = Array.isArray(keys) ? keys : [];
    const found = key == null ? -1 : list.indexOf(key);

    /* A finding that has been confirmed or dismissed LEAVES the visible list
       while its drawer stays open to report what happened. The first version
       treated that as "nowhere to step" and hid both controls, which took the
       navigation away at the exact moment somebody wants it: they have just
       finished with this one and the next is what they came for.
       Where the next one is, is not a guess. The row was at `at`, it was
       removed, so whatever now sits at `at` IS the next one and everything
       after it shifted up by one. */
    const gone = found === -1;
    /* Deliberately NOT clamped into the list. Clamping looks defensive and is
       wrong at the end: confirm the LAST finding and `at` is one past the end,
       so a clamp would pull it back onto the item before it and offer that as
       "next", stepping backwards into something already passed. Left raw, the
       bounds checks below simply report that there is nothing forward. */
    const base = gone ? Math.max(at || 0, 0) : found;
    const index = base;
    // When the row has gone, whatever now sits at its index IS the next one.
    const nextIndex = gone ? base : base + 1;
    const prevIndex = base - 1;

    return {
      index,
      total: list.length,
      gone,
      prevIndex,
      nextIndex,
      hasPrev: prevIndex >= 0 && prevIndex < list.length,
      hasNext: nextIndex >= 0 && nextIndex < list.length,
    };
  }

  function FindingDrawer({ kind, finding, nav, siteId, fullUrl, onClose, onDismiss, onFlip, onApplied }) {
    const { Drawer, Button, Radio, Banner } = DS();
    const members = finding.members || (finding.member ? [finding.member] : []);
    const [keep, setKeep] = useState(() => defaultKeepIndex(finding));
    const [applying, setApplying] = useState(false);
    const [results, setResults] = useState(null);
    /* Which of the drawer's own buttons is in flight. The screen's `busy` is
       keyed by the finding, and dismissing and reclassifying a misfiled term
       both set it to the same id, so reading `busy` alone would put "Dismissing"
       on the button somebody pressed to reclassify. */
    const [acting, setActing] = useState(null);

    /* Stepping to the next finding RESETS this drawer rather than replacing it.
     *
     * It used to be keyed on the finding, which does reset the state, but by
     * unmounting the drawer and building a new one: the panel and its backdrop
     * were torn down and rebuilt on every step, and the whole screen flashed.
     * A key is the right tool for "this is a different thing"; here it is the
     * same drawer looking at the next row.
     *
     * Everything the user could have changed or seen about the PREVIOUS finding
     * is cleared, or the next one would open showing the last one's chosen
     * keeper and, worse, the last one's result banner. */
    const findingKey = finding.key || finding.id;
    useEffect(() => {
      setKeep(defaultKeepIndex(finding));
      setResults(null);
      setActing(null);
      setApplying(false);
      // finding is deliberately not a dependency: it is rebuilt on every render
      // of the parent, so depending on it would reset the drawer mid-edit.
    }, [findingKey]);

    const key = finding.key || finding.id;
    const hasSuggestion = members.some(m => m.source === "suggestion");
    const hasLive = members.some(m => m.source === "wp");
    const rejects = members
      .map((m, i) => ({ m, i }))
      .filter(x => x.m.source === "suggestion" && x.i !== keep)
      .map(x => ({ id: x.m.id, name: x.m.name }));
    const keeper = members[keep];

    const apply = async () => {
      setApplying(true);
      const out = [];
      /* Every outcome is kept, not just the first failure. The ids rejected
         before a failure ARE rejected, and a drawer that reported only "it
         failed" would send the user back to reject them a second time. */
      for (const r of rejects) {
        try {
          await api(`/taxonomy/suggestions/${r.id}`, {
            method: "PATCH", body: JSON.stringify({ status: "rejected", site_id: siteId, siteUrl: fullUrl }),
          });
          out.push({ ...r, ok: true });
        } catch (e) { out.push({ ...r, ok: false, error: e.message || "Could not apply that." }); }
      }
      setResults(out); setApplying(false);
      onApplied(out);
    };

    const done = results && results.every(r => r.ok);
    const okNames = (results || []).filter(r => r.ok).map(r => r.name);
    const failed = (results || []).filter(r => !r.ok);

    const title = kind === "duplicate" ? "The same term twice"
      : kind === "misfiled" ? `Looks like a ${finding.suggest}`
      : "Nothing filed under this";

    return (
      <Drawer title={title} subtitle={members.map(m => m.name).join(", ")} onClose={onClose} footer={
        <>
          <Button variant="ghost" disabled={!!acting || applying} onClick={() => { setActing("dismiss"); onDismiss(); }}>
            {acting === "dismiss" ? "Dismissing…" : "Dismiss"}
          </Button>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {kind === "duplicate" && hasSuggestion && !done && (
            <Button variant="primary" disabled={applying || !!acting || rejects.length === 0} onClick={apply}>
              {applying ? "Applying…" : "Confirm"}
            </Button>
          )}
          {kind === "misfiled" && finding.member && finding.member.source === "suggestion" && (
            <Button variant="primary" disabled={!!acting} onClick={() => { setActing("flip"); onFlip(); }}>
              {acting === "flip" ? "Working…" : `Make it a ${finding.suggest}`}
            </Button>
          )}
        </>
      }>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Stepping through the list lives INSIDE the drawer, not in the
              footer. The footer is for what happens to THIS finding, and adding
              navigation there crowded out Dismiss and Close: five controls in a
              row that only has space for three, so the last of them fell out of
              the panel. Two icon buttons and a position label take a fraction of
              the room and read as navigation rather than as a decision.
              Both stop at the ends rather than wrapping, and the label says
              where you are so an end reads as an end instead of a dead control.
              chevron-left is a design-system icon and chevron-right is not, so
              the forward one is the same glyph mirrored, which also keeps the
              pair visually symmetrical. */}
          {nav && (nav.prev || nav.next) && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button className="bi-iconbtn" type="button" aria-label="Previous finding"
                disabled={!nav.prev || !!acting || applying}
                onClick={() => nav.prev && nav.prev()}>
                <MlIcon name="chevron-left" size={16} />
              </button>
              <button className="bi-iconbtn" type="button" aria-label="Next finding"
                disabled={!nav.next || !!acting || applying}
                onClick={() => nav.next && nav.next()}>
                <MlIcon name="chevron-left" size={16} style={{ transform: "scaleX(-1)" }} />
              </button>
              {/* Once this finding has left the list, "3 of 12" would be a lie
                  about a row that is no longer in it, so the count gives way to
                  what is actually true and useful: how many are left to go. */}
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                {nav.gone ? `${nav.total} left` : `${nav.index + 1} of ${nav.total}`}
              </span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {finding.level && levelBadge(finding.level)}
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{finding.why}</span>
          </div>

          {kind === "duplicate" && !hasSuggestion && (
            /* A group of live terms only has nothing to choose between: merging
               them means reassigning every post and deleting a term on a
               published site, which this app does not do. Saying so is the whole
               content of the drawer, rather than a radio list that would imply
               pressing something here could settle it. */
            <>
              {Banner
                ? <Banner tone="info" title="There is nothing to choose here">{finding.note || "Merging them rewrites published posts, so this is reported for you to do there."}</Banner>
                : <p className="bi-subempty" style={{ margin: 0 }}>{finding.note}</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{members.map((m, i) => <span key={i}>{memberChip(m)}</span>)}</div>
            </>
          )}

          {kind === "duplicate" && hasSuggestion && (
            <>
              <div>
                <div className="bi-field__label">Which one do you want to keep?</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {members.map((m, i) => (
                    <Radio key={i} name={"keep-" + key} checked={keep === i} disabled={!!results}
                      onChange={() => setKeep(i)} label={memberChip(m)} />
                  ))}
                </div>
              </div>
              {/* Said on the screen that offers the choice, not left to be
                  remembered: choosing a suggestion does not remove the live
                  term, because nothing here writes to WordPress. */}
              <p className="bi-subempty" style={{ margin: 0 }}>
                {hasLive
                  ? "Only suggestions change. Live WordPress terms are left exactly as they are, whichever one you keep."
                  : "The others are rejected, which is reversible from the Taxonomy Library. No WordPress term is touched."}
              </p>
              {!results && (
                <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                  {rejects.length === 0
                    ? "Nothing to apply: the only suggestion in this group is the one you are keeping."
                    : `Applying rejects ${rejects.map(r => `"${r.name}"`).join(", ")}${keeper ? `, and keeps "${keeper.name}"` : ""}.`}
                </p>
              )}
            </>
          )}

          {kind === "misfiled" && (
            <>
              <div>{memberChip(finding.member)}</div>
              {finding.member.source !== "suggestion" && (
                <p className="bi-subempty" style={{ margin: 0 }}>Live in WordPress, so change it there. Nothing on this screen writes to a published term.</p>
              )}
            </>
          )}

          {kind === "unused" && (
            <>
              <div>{memberChip(finding.member)}</div>
              <p className="bi-subempty" style={{ margin: 0 }}>Taxonomy Audit can put it on the posts it fits.</p>
            </>
          )}

          {/* What actually happened, in the drawer that asked for it. A partial
              run is its own state and is reported as one: the names that went
              through, and the name that did not, with the reason. */}
          {results && Banner && (
            <Banner tone={failed.length ? "danger" : "success"} title={failed.length ? "Partly applied" : "Applied"}>
              {okNames.length ? `Rejected ${okNames.join(", ")}. ` : "Nothing was rejected. "}
              {keeper && !failed.length ? `"${keeper.name}" is the one that stays.` : ""}
              {failed.map(f => <div key={f.id}>Could not reject "{f.name}": {f.error}</div>)}
            </Banner>
          )}
        </div>
      </Drawer>
    );
  }

  /* Consolidation sub-view: one screen for both faults a taxonomy grows that no
     single row can show, because both are properties of the SET. Reached from
     the Taxonomy Library rather than living in the sidebar, because it is
     something you do TO the library. */
  function ConsolidateView({ site, onBack, onChanged }) {
    const { Button, EmptyState } = DS();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const fullUrl = fullUrlOf(site);
    const [data, setData] = useState(null);
    const [phase, setPhase] = useState("loading");   // loading | idle | running | done
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);
    const [doneIds, setDoneIds] = useState([]);      // findings acted on this visit
    const [tab, setTab] = useState("duplicates");
    const [openSel, setOpenSel] = useState(null);    // { kind, key } of the drawer
    /* The scan report is what THIS run read, so it is only ever set by the POST
       below. A stored scan read back on load is not news, and a window
       announcing it every time the screen opens would be closed unread. */
    const [scanReport, setScanReport] = useState(null);

    // The last scan is read back rather than re-run, so opening the screen is
    // free and only the button spends anything.
    useEffect(() => {
      let alive = true;
      (async () => {
        if (siteId == null) { setPhase("idle"); return; }
        try {
          const d = await api(`/taxonomy/consolidate?site_id=${siteId}`);
          if (!alive) return;
          if (d && !d.never_scanned) { setData(d); setPhase("done"); } else setPhase("idle");
        } catch { if (alive) setPhase("idle"); }
      })();
      return () => { alive = false; };
    }, [siteId]);

    const run = async () => {
      if (siteId == null || !fullUrl) return;
      setPhase("running"); setError(""); setDoneIds([]); setOpenSel(null);
      try {
        const d = await api(`/taxonomy/consolidate`, { method: "POST", body: JSON.stringify({ site_id: siteId, siteUrl: fullUrl }) });
        setData(d); setScanReport(d);   // the window, once, for the run just finished
        setPhase("done");
      } catch (e) { setError(e.message || "The scan failed."); setPhase(data ? "done" : "idle"); }
    };

    // Only ever a suggestion, never a live term: see the note each finding
    // carries when nothing here can be done from the app.
    const applyFix = async (key, fixes) => {
      setBusy(key); setError("");
      try {
        for (const f of fixes) {
          await api(`/taxonomy/suggestions/${f.suggestion_id}`, {
            method: "PATCH", body: JSON.stringify({ status: "rejected", site_id: siteId, siteUrl: fullUrl }),
          });
        }
        setDoneIds(d => [...d, key]); onChanged && onChanged();
      } catch (e) { setError(e.message || "Could not apply that."); }
      finally { setBusy(null); }
    };

    const flipKind = async (finding) => {
      const m = finding.member;
      setBusy(finding.id); setError("");
      try {
        await api(`/taxonomy/suggestions/${m.id}`, {
          method: "PATCH", body: JSON.stringify({ kind: finding.suggest, site_id: siteId }),
        });
        setDoneIds(d => [...d, finding.id]); onChanged && onChanged();
      } catch (e) { setError(e.message || "Could not change that."); }
      finally { setBusy(null); }
    };

    // Dismissing hides a finding for good, across rescans. It is a judgment
    // about the finding, not a change to the terms, so it is reversible: the
    // count and the restore control are always visible when anything is hidden.
    const dismiss = async (key) => {
      setBusy(key); setError("");
      try {
        const r = await api(`/taxonomy/consolidate/dismiss`, { method: "POST", body: JSON.stringify({ site_id: siteId, key }) });
        setDoneIds(d => [...d, key]);
        setData(d => (d ? { ...d, dismissed_count: r?.dismissed_count ?? ((d.dismissed_count || 0) + 1) } : d));
      } catch (e) { setError(e.message || "Could not dismiss that."); }
      finally { setBusy(null); }
    };
    const clearDismissed = async () => {
      setBusy("clear"); setError("");
      try {
        await api(`/taxonomy/consolidate/dismiss?site_id=${siteId}`, { method: "DELETE" });
        setData(await api(`/taxonomy/consolidate?site_id=${siteId}`));
        setDoneIds([]);
      } catch (e) { setError(e.message || "Could not restore them."); }
      finally { setBusy(null); }
    };

    const dupes = (data?.duplicates || []).filter(g => !doneIds.includes(g.key));
    const misfiled = (data?.misfiled || []).filter(f => !doneIds.includes(f.id));
    const unused = (data?.unused || []).filter(f => !doneIds.includes(f.id));
    /* A term this app created when you approved it has no posts because nothing
       has been filed under it yet. That is the expected state of a batch
       approved five minutes ago, not a fault, and listing it under the same
       heading as a term that has sat empty for months makes approving a batch
       in the Library look like a batch of mistakes over here. The scan already
       tells the two apart (`fresh`); only the heading did not. */
    const unusedFresh = unused.filter(f => f.fresh);
    const unusedStale = unused.filter(f => !f.fresh);
    const dismissBtn = (key) => (
      <Button variant="secondary" size="sm" disabled={busy === key} onClick={() => dismiss(key)}>Dismiss</Button>
    );

    /* The drawer reads from the UNFILTERED scan, not from the lists above. A
       group that has just been applied is in doneIds and has left the list,
       which is what should happen behind the drawer; looking it up in the
       filtered list would unmount the drawer at the moment it has something to
       report. */
    const findingFor = (sel) => {
      if (!sel || !data) return null;
      const src = sel.kind === "duplicate" ? (data.duplicates || [])
        : sel.kind === "misfiled" ? (data.misfiled || []) : (data.unused || []);
      return src.find(f => (f.key || f.id) === sel.key) || null;
    };
    const openFinding = findingFor(openSel);

    /* Walking to the next finding without closing.
     *
     * The order is the VISIBLE order of the tab the finding belongs to, not the
     * scan's order: the drawer reads from the unfiltered scan so it survives its
     * own apply (see findingFor), but "next" has to mean the next card the user
     * can actually see, or it would jump to something dismissed or filtered
     * away. The unused tab holds two sections and walks them in the order they
     * render, stale before fresh.
     *
     * A finding the user just acted on has left the visible list, so its index
     * is -1 and both ends read as unavailable. That is honest rather than
     * convenient: guessing where it "would have been" could send the user
     * backwards through work already done. */
    const siblingKeys = (kind) => (
      kind === "duplicate" ? dupes.map(g => g.key)
        : kind === "misfiled" ? misfiled.map(f => f.id)
          : [...unusedStale, ...unusedFresh].map(f => f.id)
    );
    const sibs = openSel ? siblingKeys(openSel.kind) : [];
    // `at` is where the open finding sat when it was opened, which is what makes
    // stepping still work after it has been acted on and left the list.
    const pos = navPosition(sibs, openSel && openSel.key, openSel && openSel.at);
    const stepTo = (i) => setOpenSel({ kind: openSel.kind, key: sibs[i], at: i });
    const nav = {
      index: pos.index, total: pos.total, gone: pos.gone,
      prev: pos.hasPrev ? () => stepTo(pos.prevIndex) : null,
      next: pos.hasNext ? () => stepTo(pos.nextIndex) : null,
    };

    /* What the drawer did, reported where this screen already reports things.
       Naming the term matters: "could not apply that" leaves a user holding a
       group of five to work out which one is still there. */
    const afterApply = (key, results) => {
      if (results.some(r => r.ok)) onChanged && onChanged();
      const failed = results.filter(r => !r.ok);
      if (!failed.length) { setDoneIds(d => (d.includes(key) ? d : [...d, key])); setError(""); return; }
      setError(failed.map(f => `Could not reject "${f.name}": ${f.error}`).join(" "));
    };

    const TABS = [
      { id: "duplicates", label: "Duplicates", count: dupes.length },
      { id: "misfiled", label: "Tag or category?", count: misfiled.length },
      /* The count is the stale terms only, deliberately. A term approved this
         week is empty by definition, and folding those into the number would
         put a batch somebody just approved on the tab as a count of faults,
         which is the exact reading the fresh/stale split exists to prevent.
         They keep their own heading inside the tab. */
      { id: "unused", label: "Nothing filed under these", count: unusedStale.length },
    ];

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <button className="bi-detail__back" onClick={onBack}><MlIcon name="chevron-left" size={16} />Back to Taxonomy Library</button>
            <h1 className="ml-pagehead__title">Consolidate taxonomy</h1>
            <p className="ml-pagehead__sub">
              The same term entered twice, and terms filed as the wrong kind, across your live WordPress categories and tags and the suggestions here.
            </p>
          </div>
          <div className="ml-pagehead__actions">
            <Button variant="primary" leadingIcon="refresh" disabled={phase === "running" || siteId == null} onClick={run}>
              {phase === "running" ? "Scanning…" : data ? "Scan again" : "Scan"}
            </Button>
          </div>
        </div>

        {error && <div className="bi-subempty" style={{ color: "var(--danger)" }}>{error}</div>}

        {/* The counts a scan read are news about that run and live in the window
            below, once. These do not: they say whether the list underneath is
            the whole answer, so they are read every time it is read, including
            on a stored scan the window never fires for. A partial scan that
            looked complete on the second visit is the failure worth guarding
            against here. */}
        {data && (data.ai_error || data.ai_unreadable || data.ai_not_reviewed > 0 || data.dismissed_count > 0) && (
          <div className="bi-subempty" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {aiNotes(data)}
            {/* Always visible when anything is hidden: a screen that silently
                withholds findings is worse than one that shows too many. */}
            {data.dismissed_count > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {plural(data.dismissed_count, "finding", "findings")} dismissed.
                <button className="bi-btn bi-btn--sm" disabled={busy === "clear"} onClick={clearDismissed}>{busy === "clear" ? "Restoring…" : "Show them again"}</button>
              </span>
            )}
          </div>
        )}

        {/* What the scan just read, once, for the run the user asked for. */}
        {scanReport && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: "Scan finished", onClose: () => setScanReport(null),
          foot: <button className="bi-btn bi-btn--primary" onClick={() => setScanReport(null)}>Got it</button>,
        }, (
          <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              Read {scanReport.scanned?.categories || 0} categories, {scanReport.scanned?.tags || 0} tags and {scanReport.scanned?.suggestions || 0} suggestions.
              {/* An approved suggestion IS its live term, so it is not compared
                  against itself. Said rather than silently subtracted. */}
              {scanReport.scanned?.linked > 0 && ` ${scanReport.scanned.linked} of those suggestions are the live terms they created, so they are counted once.`}
            </p>
            <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
              {/* The findings are behind tabs now, so the window says what is on
                  each of them rather than leaving the user to click three. */}
              {plural((scanReport.duplicates || []).length, "duplicate", "duplicates")},
              {" "}{(scanReport.misfiled || []).length} possibly filed as the wrong kind,
              {" "}{(scanReport.unused || []).filter(f => !f.fresh).length} with nothing filed under them.
            </p>
            {/* Repeated here as well as on the screen: the difference between a
                model that found nothing and a model that never ran belongs in
                the report of the run it did or did not happen in. */}
            {(scanReport.ai_error || scanReport.ai_unreadable || scanReport.ai_not_reviewed > 0) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "var(--text-sm)" }}>{aiNotes(scanReport)}</div>
            )}
          </div>
        ))}

        {phase === "loading" ? <div className="bi-subempty">Loading…</div>
          : !data ? (
            <div className="hds-table-wrap" style={{ padding: 8 }}>
              {EmptyState
                ? <EmptyState icon="refresh" title="Not scanned yet" description="Scan to find duplicate terms and terms filed as the wrong kind. Reading is free; nothing changes until you act on a finding." />
                : <div className="bi-subempty">Not scanned yet.</div>}
            </div>
          ) : (
            <>
              {/* Three sections that were stacked, so a screen with a long list
                  of duplicates buried the other two below the fold. */}
              <div className="hds-tabs" role="tablist" style={{ margin: "18px 0 12px" }}>
                {TABS.map(t => (
                  <button key={t.id} role="tab" aria-selected={tab === t.id}
                    className={["hds-tab", tab === t.id ? "hds-tab--active" : ""].filter(Boolean).join(" ")}
                    onClick={() => setTab(t.id)}>
                    {t.label}<span className="hds-tab__count">{t.count}</span>
                  </button>
                ))}
              </div>

              {tab === "duplicates" && (dupes.length === 0 ? <div className="bi-subempty">Nothing repeated. Every term is distinct.</div> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {dupes.map((g, gi) => (
                    <div key={g.key} {...findingCardProps(
                      `Review duplicate: ${g.members.map(m => m.name).join(", ")}`,
                      () => setOpenSel({ kind: "duplicate", key: g.key, at: gi }),
                      openSel && openSel.kind === "duplicate" && openSel.key === g.key)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                        {levelBadge(g.level)}
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{g.why}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>{g.members.map((m, i) => <span key={i}>{memberChip(m)}</span>)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{g.note}</span>
                        <div style={{ flex: 1 }} />
                        {dismissBtn(g.key)}
                        {g.fixes?.length > 0 && (
                          <Button variant="secondary" size="sm" leadingIcon="x" disabled={busy === g.key} onClick={() => applyFix(g.key, g.fixes)}>
                            {busy === g.key ? "Working…" : `Reject ${plural(g.fixes.length, "duplicate", "duplicates")}`}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {tab === "misfiled" && (misfiled.length === 0 ? (
                <div className="bi-subempty">
                  {/* Two different reasons for an empty section, and they are
                      not the same news. Post counts can only support a claim
                      about kind comparatively, so a site with too few posts
                      gets no such claim rather than a fabricated one. */}
                  {data.scanned?.posts == null
                    ? "The site's post count could not be read, so nothing here is judged by usage. The AI review still ran."
                    : data.scanned.posts < 20
                      ? `Only ${data.scanned.posts} published posts, too few for a share of the site to mean anything, so nothing is judged by usage here.`
                      : "Nothing looks filed the wrong way round."}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {misfiled.map((f, fi) => (
                    <div key={f.id} {...findingCardProps(
                      `Review ${f.member.name}, which looks like a ${f.suggest}`,
                      () => setOpenSel({ kind: "misfiled", key: f.id, at: fi }),
                      openSel && openSel.kind === "misfiled" && openSel.key === f.id)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                        {levelBadge(f.level)}
                        <span style={{ fontSize: "var(--text-sm)" }}>Looks like a <strong>{f.suggest}</strong></span>
                      </div>
                      <div style={{ marginBottom: 10 }}>{memberChip(f.member)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{f.why}</span>
                        <div style={{ flex: 1 }} />
                        {dismissBtn(f.id)}
                        {f.member.source === "suggestion"
                          ? <Button variant="secondary" size="sm" leadingIcon="refresh" disabled={busy === f.id} onClick={() => flipKind(f)}>
                              {busy === f.id ? "Working…" : `Make it a ${f.suggest}`}
                            </Button>
                          : <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Live in WordPress, so change it there.</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* A term approved last week has no posts because nobody has filed
                  anything under it yet. That is not a mistake about its kind, so
                  it is reported apart with the fix that actually applies: put it
                  on some posts, which is what Taxonomy Audit does. Both halves
                  share this tab, and keep the headings that tell them apart. */}
              {tab === "unused" && (
                <>
                  {unusedStale.length === 0
                    ? <div className="bi-subempty">Every term that has been around a while has something filed under it.</div>
                    : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {/* The tab already says this, and the heading stays
                            anyway: the panel holds two sections and the second
                            one has a heading, so dropping this one would leave
                            the first list looking like part of the second. */}
                        <h2 className="bi-title" style={{ fontSize: "var(--text-lg)", margin: "4px 0 0" }}>Nothing filed under these ({unusedStale.length})</h2>
                        {unusedStale.map((f, fi) => (
                          <div key={f.id} {...findingCardProps(
                            `Review ${f.member.name}, which has nothing filed under it`,
                            () => setOpenSel({ kind: "unused", key: f.id, at: fi }),
                            openSel && openSel.kind === "unused" && openSel.key === f.id)}>
                            <div style={{ marginBottom: 10 }}>{memberChip(f.member)}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{f.why} Taxonomy Audit can put it on the posts it fits.</span>
                              <div style={{ flex: 1 }} />
                              {dismissBtn(f.id)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Its own heading, and a heading that says what actually
                      happened rather than what is missing. A category you
                      approved this week is empty by definition, so counting it
                      as a finding, or in the tab's count, would mean every
                      approved batch arrives here looking like a mistake and the
                      whole screen stops being worth reading. */}
                  {unusedFresh.length > 0 && (
                    <>
                      <h2 className="bi-title" style={{ fontSize: "var(--text-lg)", margin: "22px 0 8px" }}>Approved recently, not on any post yet ({unusedFresh.length})</h2>
                      <p className="bi-subempty" style={{ margin: "0 0 8px" }}>
                        Created in WordPress when you approved them. Nothing is wrong with these: they are waiting to be applied, which is what Taxonomy Audit does.
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {unusedFresh.map((f, fi) => (
                          <div key={f.id} {...findingCardProps(
                            `Review ${f.member.name}, approved recently and not on any post yet`,
                            () => setOpenSel({ kind: "unused", key: f.id, at: unusedStale.length + fi }),
                            openSel && openSel.kind === "unused" && openSel.key === f.id)}>
                            <div style={{ marginBottom: 10 }}>{memberChip(f.member)}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{f.why} Taxonomy Audit can put it on the posts it fits.</span>
                              <div style={{ flex: 1 }} />
                              {dismissBtn(f.id)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

        {/* Deliberately NOT keyed on the finding. A key would reset the drawer
            by unmounting it, which tore down the panel and its backdrop on every
            step and flashed the whole screen. The drawer clears its own state
            when the finding changes instead: see the effect in FindingDrawer. */}
        {openFinding && (
          <FindingDrawer kind={openSel.kind} finding={openFinding} nav={nav}
            siteId={siteId} fullUrl={fullUrl}
            onClose={() => setOpenSel(null)}
            onDismiss={async () => { await dismiss(openSel.key); setOpenSel(null); }}
            onFlip={async () => { await flipKind(openFinding); setOpenSel(null); }}
            onApplied={(results) => afterApply(openSel.key, results)} />
        )}
      </div>
    );
  }

  // ---- Taxonomy Suggestions: AI category/tag suggestions → draft → approve ----
  const SUGG_TABS = [
    { id: "draft",    label: "Draft" },
    { id: "approved", label: "Approved" },
    { id: "in_use",   label: "In use" },
    { id: "rejected", label: "Rejected" },
    { id: "archived", label: "Archived" },
  ];
  /* What a generation run can be grounded in. Posts and pages are read live from
     WordPress; keywords and topics come from the site's own banks. The labels
     say where each one lives, because "pages" and "topics" both sound like
     things this app owns and only one of them is. */
  const SUGG_SOURCES = [
    { id: "posts",    label: "Posts",    hint: "Published posts on the site" },
    // Not "classified keywords" and not "ready topics", which is what these
    // said while the run had already stopped requiring either. Classifying
    // needs a vocabulary and this is what proposes one, so a bank waiting to be
    // classified is exactly the material a run has to read.
    { id: "pages",    label: "Pages",    hint: "Published pages on the site" },
    { id: "keywords", label: "Keywords", hint: "Keywords in the planner, including ones not classified yet" },
    { id: "topics",   label: "Topics",   hint: "Topics in the bank, including ones not judged yet" },
    /* And the business register, which is four modules rather than one source.
       "Products" here is the register of offerings and NOT a WordPress post
       type, which is the reading to head off on a screen that also lists Posts
       and Pages, so the hint says where it lives rather than what it is.
       Competitors and Partners say out loud that they can only produce tags.
       Ticking them expecting new sections of the site would be a
       disappointment, and the reason fits on one line: a category is this
       site's own navigation, and a rival's register is not that. */
    { id: "products",    label: "Products",    hint: "Offerings in your Products register, not a WordPress post type" },
    { id: "industry",    label: "Industries",  hint: "Industries your business serves, from the Industry register" },
    { id: "competitors", label: "Competitors", hint: "Tracked competitors, read for tags only: categories stay your own" },
    { id: "partners",    label: "Partners",    hint: "Tracked partners, read for tags only: categories stay your own" },
  ];

  /* What a run read, per source, split three ways for the report. A module that
     could not be read is kept out of the "read from" sentence and named on its
     own: "0 products" and "the register could not be opened" send the user to
     two different places, and only one of those errands is worth walking. */
  const readSources    = (used) => SUGG_SOURCES.filter(s => used[s.id] && !used[s.id].unreadable && !used[s.id].skipped);
  const failedSources  = (used) => SUGG_SOURCES.filter(s => used[s.id] && used[s.id].unreadable);

  /* Where ONE suggestion came from, which is not the question "what did this
     run read". A category proposed off a spreadsheet row nobody has checked and
     a category proposed off what the site actually publishes are worth
     different amounts of trust, and approving creates a real WordPress term, so
     the difference belongs on the row and not in a run summary.

     Published content is what the site has already committed to. Bank material
     is a plan or an import, so it is toned as something to look at rather than
     something settled. */
  const SUGG_FROM = {
    posts:    { label: "From your posts", tone: "success", hint: "Grounded in posts already published on the site" },
    pages:    { label: "From your pages", tone: "success", hint: "Grounded in pages already published on the site" },
    keywords: { label: "From keywords",   tone: "warning", hint: "Grounded in the keyword bank, which may be an unvetted import" },
    topics:   { label: "From topics",     tone: "warning", hint: "Grounded in the topics bank, which is research rather than published content" },
    /* The register describes businesses rather than pages, so it gets its own
       reading. Products and industries describe THIS business: recorded rather
       than published, but the user's own either way. Competitors and partners
       describe other companies, which is why they can only ever be a tag, and
       the chip says so on the row rather than leaving it to be remembered. */
    products:    { label: "From products",    tone: "info",    hint: "Grounded in an offering you record in the Products register" },
    industry:    { label: "From industries",  tone: "info",    hint: "Grounded in an industry your business serves" },
    competitors: { label: "From competitors", tone: "warning", hint: "Grounded in a tracked competitor, which can only produce a tag" },
    partners:    { label: "From partners",    tone: "warning", hint: "Grounded in a tracked partner, which can only produce a tag" },
  };
  /* A suggestion whose origin the run could not establish says so. Guessing the
     likeliest source would destroy the only thing the chip is for, and "we do
     not know" is a fact a reviewer can act on. */
  function fromChip(it) {
    const src = it && it.source && typeof it.source === "object" ? it.source : null;
    const f = src && SUGG_FROM[src.from];
    if (!f) return <span className="hds-table__muted" title="The run did not record which material drove this suggestion.">Not attributed</span>;
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
        <span className={"hds-badge hds-badge--" + f.tone} title={f.hint}>{f.label}</span>
        {src.cluster && <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }} title="The keyword cluster this came out of">{src.cluster}</span>}
        {/* The register row itself, when the run could confirm one. "From
            products" is a great deal less use to a reviewer than "From
            products, VAT filing": the second names something they can go and
            look at before approving a term into WordPress. */}
        {src.entity && <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }} title="The register row this came out of">{src.entity}</span>}
      </span>
    );
  }

  /* What a bulk run can do to a row depends on where that row already is, and
     the answer is taken from the per-row buttons rather than invented: a bulk
     action that offered a transition the row itself does not is a second, wrong
     definition of the workflow. Delete is added to every status because the row
     offers it everywhere too.

     Icons here are DS names only. leadingIcon goes through the DS Icon, not
     MlIcon, so an extras-only name (archive, rotate-ccw, check-square) renders
     as an empty svg. Archive has no DS equivalent worth borrowing, so it goes
     without one rather than wearing a misleading glyph. */
  const A = {
    approve: { status: "approved", label: "Approve", gerund: "Approving", past: "approved", variant: "primary",   icon: "check" },
    reject:  { status: "rejected", label: "Reject",  gerund: "Rejecting", past: "rejected", variant: "secondary", icon: "x" },
    archive: { status: "archived", label: "Archive", gerund: "Archiving", past: "archived", variant: "secondary" },
    restore: { status: "draft",    label: "Restore", gerund: "Restoring", past: "restored", variant: "secondary", icon: "refresh" },
  };
  // Removes the suggestion and leaves any WordPress term a past approval
  // created standing.
  //
  // Deliberately NOT offered on drafts. A draft you do not want is a Reject,
  // which is the same outcome and keeps the record of having decided; deleting
  // it instead throws that away and lets the next generation run propose the
  // very same term again with nothing to say it was already turned down.
  const A_DELETE = { status: "delete", label: "Delete", gerund: "Deleting", past: "deleted", variant: "secondary", icon: "trash", danger: true };
  const SUGG_BULK = {
    draft:    [A.approve, A.reject],
    approved: [A.archive, A_DELETE],
    in_use:   [A.archive, A_DELETE],
    rejected: [A.restore, A_DELETE],
    archived: [A.restore, A_DELETE],
  };

  /* ---- Consolidation review ----
     Findings come in two kinds and are never mixed: a string match is a fact,
     a model's opinion that two different names mean one thing is a question.
     The badge is the whole point of the screen, so it is not decoration. */
  const LEVEL_BADGE = {
    exact:      { tone: "success", label: "Same name" },
    normalised: { tone: "info",    label: "Same once tidied" },
    proposed:   { tone: "warning", label: "AI suggests" },
    signal:     { tone: "info",    label: "By usage" },
  };
  function levelBadge(level) {
    const b = LEVEL_BADGE[level] || { tone: "neutral", label: level };
    return <span className={"hds-badge hds-badge--" + b.tone}>{b.label}</span>;
  }
  // Where a member lives decides what can be done about it, so it is stated on
  // every row rather than left to be inferred from which button appeared.
  function memberChip(m) {
    const live = m.source === "wp";
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: "var(--radius-sm)", background: "var(--bg-subtle)", fontSize: "var(--text-sm)" }}>
        <strong>{m.name}</strong>
        <span className="hds-badge hds-badge--neutral">{m.kind === "category" ? "Category" : "Tag"}</span>
        <span style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>
          {live ? `in WordPress${m.count != null ? `, ${m.count} posts` : ""}` : `suggestion, ${m.status}`}
        </span>
      </span>
    );
  }

  function suggStatusBadge(s) {
    const map = { draft: "draft", approved: "info", in_use: "success", rejected: "danger", archived: "neutral" };
    const label = { draft: "Draft", approved: "Approved", in_use: "In use", rejected: "Rejected", archived: "Archived" }[s] || s;
    return <span className={"hds-badge hds-badge--" + (map[s] || "neutral")}><span className="hds-badge__dot" />{label}</span>;
  }

  function TaxonomySuggestionsScreen({ site }) {
    const { Button, IconButton, EmptyState } = DS();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const fullUrl = fullUrlOf(site);
    const enc = encodeURIComponent(fullUrl);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("draft");
    const [kind, setKind] = useState("all");        // all | category | tag
    const [gen, setGen] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(null);   // { gerund, done, total } while a bulk run is in flight
    const [report, setReport] = useState(null);       // what the last bulk run actually did
    const [sources, setSources] = useState(SUGG_SOURCES.map(s => s.id));  // what to ground generation in
    const [genOpen, setGenOpen] = useState(false);    // the choose-and-confirm dialog
    const [genReport, setGenReport] = useState(null); // what the last generate run read and added
    const [includeRead, setIncludeRead] = useState(false); // go back over material already used
    const [avail, setAvail] = useState(null);         // per-source unread counts, for the dialog
    const toggleSource = (id) => setSources(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    // Selecting is a mode you enter, as on the other modules, rather than a
    // checkbox column sitting in the table whether or not anyone wants it.
    const [selectMode, setSelectMode] = useState(false);
    const leaveSelect = () => { setSelectMode(false); setSelected([]); };
    // Deleting many rows at once cannot be undone, so it asks first. The other
    // bulk actions are status moves and every one of them is reversible from
    // the tab it lands in, so they run on the click.
    const [confirmBulk, setConfirmBulk] = useState(null);
    const [consolidating, setConsolidating] = useState(false);

    const load = useCallback(async () => {
      if (siteId == null || !fullUrl) { setItems([]); setLoading(false); return; }
      setLoading(true);
      try {
        const data = await api(`/taxonomy/suggestions?site_id=${siteId}&siteUrl=${enc}`);
        setItems(Array.isArray(data && data.suggestions) ? data.suggestions : []);
      } catch (e) { setError(e.message || "Failed to load suggestions"); setItems([]); }
      finally { setLoading(false); }
    }, [siteId, enc, fullUrl]);
    useEffect(() => { load(); }, [load]);

    // Asked when the dialog opens, so the counts describe the moment the user is
    // deciding rather than whenever the screen last loaded.
    const openGenerate = async () => {
      setGenOpen(true); setAvail(null);
      if (siteId == null) return;
      try { setAvail(await api(`/taxonomy/suggest/status?site_id=${siteId}&siteUrl=${enc}`)); }
      catch { setAvail(null); }   // the dialog still works without them
    };

    const generate = async () => {
      if (siteId == null || !fullUrl || !sources.length) return;
      setGenOpen(false);
      setGen(true); setError(""); setReport(null); setGenReport(null);
      try {
        const r = await api(`/taxonomy/suggest`, { method: "POST", body: JSON.stringify({ site_id: siteId, siteUrl: fullUrl, sources, include_read: includeRead }) });
        setGenReport(r || null);
        await load();
      }
      catch (e) { setError(e.message || "Could not generate suggestions"); }
      finally { setGen(false); }
    };
    const patch = async (id, status) => {
      setBusyId(id); setError("");
      try { await api(`/taxonomy/suggestions/${id}`, { method: "PATCH", body: JSON.stringify({ status, site_id: siteId, siteUrl: fullUrl }) }); await load(); }
      catch (e) { setError(e.message || "Action failed"); }
      finally { setBusyId(null); }
    };
    const remove = async (id) => {
      setBusyId(id);
      try { await api(`/taxonomy/suggestions/${id}?site_id=${siteId}`, { method: "DELETE" }); await load(); }
      catch (e) { setError(e.message || "Delete failed"); }
      finally { setBusyId(null); }
    };

    const eff = (it) => it.effective_status || it.status;
    const counts = SUGG_TABS.reduce((m, t) => { m[t.id] = items.filter(i => eff(i) === t.id).length; return m; }, {});
    const view = items.filter(i => eff(i) === tab && (kind === "all" || i.kind === kind));

    // Selection is scoped to what is on screen. Switching tab or filter changes
    // which rows exist, so a selection carried across would act on rows the user
    // can no longer see.
    useEffect(() => { setSelected([]); setReport(null); }, [tab, kind]);

    // Below every hook, deliberately: an early return above one of them changes
    // the number of hooks between renders and React throws on the toggle.
    // Reloads on the way back, because consolidating rejects and reclassifies
    // rows this list is showing.
    if (consolidating) return <ConsolidateView site={site} onBack={() => { setConsolidating(false); load(); }} onChanged={load} />;

    const allChecked = view.length > 0 && view.every(r => selected.includes(r.id));
    const toggleAll = () => setSelected(allChecked ? [] : view.map(r => r.id));
    const toggleOne = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    /* The offered actions come from the tab, because every row in a view shares
       its status. Rows in the selection that have since moved elsewhere are
       counted and named rather than quietly acted on or quietly dropped. */
    const bulkActions = SUGG_BULK[tab] || [];
    const selectedRows = items.filter(i => selected.includes(i.id));
    const eligible = selectedRows.filter(i => eff(i) === tab);
    const ineligible = selectedRows.length - eligible.length;

    const BULK_CHUNK = 10; // approving writes to WordPress per row, so report progress as it goes

    const runBulk = async (act) => {
      if (siteId == null || !eligible.length) return;
      const { status, gerund, past } = act;
      const isDelete = status === "delete";
      const ids = eligible.map(i => i.id);
      // Every status change but approval is local, so it goes in one request.
      // Approving creates a WordPress term per row: chunked, so the screen can
      // say how far it has got instead of sitting on one long silent request.
      const chunks = status === "approved"
        ? Array.from({ length: Math.ceil(ids.length / BULK_CHUNK) }, (_, i) => ids.slice(i * BULK_CHUNK, i * BULK_CHUNK + BULK_CHUNK))
        : [ids];
      setBulkBusy({ gerund, done: 0, total: ids.length });
      setError(""); setReport(null);
      let ok = 0;
      let gone = 0;
      const failures = [];
      try {
        for (const chunk of chunks) {
          const r = await api(`/taxonomy/suggestions/bulk`, {
            method: "POST",
            body: JSON.stringify(isDelete
              ? { site_id: siteId, ids: chunk, action: "delete" }
              : { site_id: siteId, siteUrl: fullUrl, ids: chunk, status }),
          });
          ok += (r && r.succeeded) || 0;
          // A row the server could not find was deleted by someone else since
          // this list was drawn. Counted separately so ok + failed + gone always
          // equals what was attempted, and the arithmetic never quietly loses a row.
          gone += ((r && r.missing) || []).length;
          for (const x of ((r && r.results) || [])) if (!x.ok) failures.push(x);
          setBulkBusy(b => (b ? { ...b, done: Math.min(b.done + chunk.length, ids.length) } : b));
        }
        setReport({ past, ok, gone, failures, attempted: ids.length });
        // The run is done, so the mode has served its purpose. Matches the
        // Keywords Planner, which also drops out of Select after a bulk action.
        leaveSelect();
      } catch (e) {
        // The request itself failed, so the rows in flight have no verdict. Say
        // how far it got rather than implying the whole run succeeded or failed.
        setError(`Stopped after ${ok} of ${ids.length} ${past}: ${e.message || "the request was rejected."}`);
        setReport(failures.length ? { past, ok, gone, failures, attempted: ids.length } : null);
      } finally {
        setBulkBusy(null);
        await load();
      }
    };

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">Taxonomy Library</h1>
            <p className="ml-pagehead__sub">AI-suggested categories and tags{site ? " for " + site.name : ""}. Approve one to create it in WordPress.</p>
          </div>
          <div className="ml-pagehead__actions">
            {/* leadingIcon goes through the DS Icon, NOT MlIcon, so it only
                accepts names in the DS set. "check-square" is one of MlIcon's
                extras: the DS renders it as an empty svg, which is why this
                button appeared to have no icon. */}
            <Button variant="secondary" leadingIcon={selectMode ? "x" : "check-circle"} disabled={gen} onClick={() => (selectMode ? leaveSelect() : setSelectMode(true))}>{selectMode ? "Cancel" : "Select"}</Button>
            <Button variant="secondary" leadingIcon="filter" disabled={gen} onClick={() => setConsolidating(true)}>Consolidate</Button>
            <Button variant="primary" leadingIcon="star" disabled={gen || siteId == null} onClick={openGenerate}>{gen ? "Generating…" : "Generate suggestions"}</Button>
          </div>
        </div>

        {/* Choose-and-confirm rather than a row of ticks beside the button: the
            sources decide what the run is grounded in and the run costs money,
            so it is worth a deliberate step rather than a setting you can leave
            wrong without noticing. */}
        {confirmBulk && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: `Delete ${plural(eligible.length, "suggestion", "suggestions")}?`, onClose: () => setConfirmBulk(null),
          foot: <>
            <button className="bi-btn" onClick={() => setConfirmBulk(null)}>Cancel</button>
            <button className="bi-btn bi-btn--danger" onClick={() => { const a = confirmBulk; setConfirmBulk(null); runBulk(a); }}>
              <MlIcon name="trash" size={16} />Delete
            </button>
          </>,
        }, (
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8, fontSize: "var(--text-sm)" }}>
            This removes {eligible.length === 1 ? "the suggestion" : "them"} from the library and cannot be undone.
            Any WordPress category or tag an approval already created stays where it is.
          </div>
        ))}

        {genOpen && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: "Generate suggestions", onClose: () => setGenOpen(false),
          foot: <>
            <button className="bi-btn" onClick={() => setGenOpen(false)}>Cancel</button>
            <button className="bi-btn bi-btn--primary" disabled={!sources.length} onClick={generate}>
              <MlIcon name="star" size={16} />Generate
            </button>
          </>,
        }, (
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8 }}>
            <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
              Suggestions are grounded in what you pick. Anything left unticked is not read at all.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {SUGG_SOURCES.map(s => {
                // Each source says how much of it this run would actually read,
                // so a tick that would contribute nothing is visible before the
                // spend rather than explained afterwards.
                const a = avail && avail[s.id];
                // A module that could not be read must not sit here as a
                // confident zero: ticking it would spend a run on nothing, and
                // unticking it would be a decision made on a wrong number.
                const broken = !!(a && a.unreadable);
                const unread = a && a.unread != null ? a.unread : null;
                const note = broken
                  ? "could not be read"
                  : includeRead
                    ? (a && a.total != null ? `${a.total} in total` : null)
                    : (unread != null ? (unread === 0 ? "all read already" : `${unread} not yet read`) : null);
                const noteColor = broken ? "var(--danger)"
                  : (unread === 0 && !includeRead ? "var(--text-muted)" : "var(--primary)");
                return (
                  <label key={s.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <span className="hds-check" style={{ marginTop: 2 }}>
                      <input type="checkbox" checked={sources.includes(s.id)} onChange={() => toggleSource(s.id)} />
                      <span className="hds-check__box"><MlIcon name="check" size={12} /></span>
                    </span>
                    <span>
                      <span style={{ display: "block", fontSize: "var(--text-sm)", fontWeight: 600 }}>
                        {s.label}
                        {note && <span style={{ fontWeight: 400, color: noteColor }}> · {note}</span>}
                      </span>
                      <span style={{ display: "block", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{s.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Each item is read once and then marked, so a second run works on
                what arrived since rather than on the same head of the list. This
                is the way back over material already used. */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-subtle)", cursor: "pointer" }}>
              <span className="hds-check" style={{ marginTop: 2 }}>
                <input type="checkbox" checked={includeRead} onChange={() => setIncludeRead(v => !v)} />
                <span className="hds-check__box"><MlIcon name="check" size={12} /></span>
              </span>
              <span>
                <span style={{ display: "block", fontSize: "var(--text-sm)", fontWeight: 600 }}>Include material already read</span>
                <span style={{ display: "block", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                  Off, a run reads only what is new since the last one. On, it goes over everything again.
                </span>
              </span>
            </label>
            {!sources.length && (
              <div style={{ marginTop: 12, fontSize: "var(--text-sm)", color: "var(--danger)" }}>
                Pick at least one source.
              </div>
            )}
          </div>
        ))}

        {/* What the run actually read, as a dialog rather than a line above the
            table. A generation run costs money and takes a while, so its result
            is the thing the user came back for: a strip that can be scrolled
            past, or replaced by the next thing that sets it, is the wrong
            weight for it. Dismissing is the acknowledgement.

            A thin result from a site whose posts never loaded looks exactly like
            a site with little left to suggest, unless the counts are shown. */}
        {genReport && React.createElement(window.MlBiModals._Modal, {
          sm: true, title: genReport.inserted ? "Suggestions added" : "Nothing new to add", onClose: () => setGenReport(null),
          foot: <button className="bi-btn bi-btn--primary" onClick={() => setGenReport(null)}>Close</button>,
        }, (
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8, fontSize: "var(--text-sm)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <MlIcon name={genReport.inserted ? "check" : "info"} size={16} style={{ marginTop: 2, color: genReport.inserted ? "var(--success)" : "var(--text-muted)" }} />
              <span>
                {genReport.inserted
                  ? `Added ${genReport.inserted} new suggestion${genReport.inserted === 1 ? "" : "s"}`
                  : "No new suggestions"}
                {genReport.generated > genReport.inserted ? ` (${genReport.generated - genReport.inserted} already existed)` : ""}
                {genReport.used && readSources(genReport.used).length
                  ? `, read from ${readSources(genReport.used).map(s => {
                      const u = genReport.used[s.id];
                      return `${u.read}${u.total != null ? ` of ${u.total}` : ""} ${s.label.toLowerCase()}`;
                    }).join(", ")}.`
                  : "."}
                {/* A module that could not be read is named as that, and never
                    folded into the counts above as a zero. Nothing from it was
                    used, and nothing from it was marked read, so the next run
                    will try it again. */}
                {genReport.used && failedSources(genReport.used).length > 0 && (
                  <> Could not read {failedSources(genReport.used).map(s => s.label.toLowerCase()).join(", ")}, so nothing from {failedSources(genReport.used).length === 1 ? "it" : "them"} was used or marked as read.</>
                )}
                {/* What the rejected filter held back. A rejected register row
                    is a decision somebody already took, so it must not shape the
                    taxonomy, but a run that skipped 12 of 40 products should say
                    so rather than look like a register of 28. */}
                {genReport.used && SUGG_SOURCES.some(s => (genReport.used[s.id]?.rejected ?? 0) > 0) && (
                  <> Left out as rejected: {SUGG_SOURCES.filter(s => (genReport.used[s.id]?.rejected ?? 0) > 0)
                    .map(s => `${genReport.used[s.id].rejected} ${s.label.toLowerCase()}`).join(", ")}.</>
                )}
                {/* Offerings nobody here provides informed the tags and not the
                    categories, on purpose: a category for something you do not
                    sell is a section of the site with nothing to file under it. */}
                {genReport.used?.products && genReport.used.products.read > (genReport.used.products.for_categories ?? 0) && (
                  <> {genReport.used.products.for_categories} of the products read could shape a category; the other {genReport.used.products.read - genReport.used.products.for_categories} are offerings you do not provide, so they informed tags only.</>
                )}
                {/* A cap stopping short is said out loud, or a partial sweep reads
                    as a complete one and the rest is never asked for again. */}
                {genReport.used && SUGG_SOURCES.some(s => (genReport.used[s.id]?.remaining ?? 0) > 0) && (
                  <> Still unread: {SUGG_SOURCES.filter(s => (genReport.used[s.id]?.remaining ?? 0) > 0)
                    .map(s => `${genReport.used[s.id].remaining} ${s.label.toLowerCase()}`).join(", ")}. Run again to continue.</>
                )}
                {/* A suggestion the run could not attribute is named here as
                    well as on its row, because the count is what says whether
                    the "From" column can be trusted at all this run. */}
                {genReport.unattributed > 0 && (
                  <> {genReport.unattributed} of them could not say which material they came from.</>
                )}
              </span>
            </div>

            {/* The grouping pass, reported apart from the suggestions. It is
                the reason the categories are few, and when it fails the
                categories were proposed from a flat list instead, which is a
                different run and must not read like the same one. */}
            {genReport.grouping && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
                {genReport.grouping.ok && (genReport.clusters || []).length > 0 ? (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      Grouped {genReport.grouping.placed} of {genReport.grouping.sent} terms into {genReport.clusters.length} cluster{genReport.clusters.length === 1 ? "" : "s"}
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)", marginBottom: 8 }}>
                      Categories were proposed from these groups rather than from the terms one by one.
                      {genReport.grouping.unplaced > 0 && ` ${genReport.grouping.unplaced} term${genReport.grouping.unplaced === 1 ? " fitted" : "s fitted"} no cluster.`}
                      {genReport.grouping.of > genReport.grouping.sent && ` ${genReport.grouping.of - genReport.grouping.sent} were read but not sent to the grouping pass.`}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {/* Keyed by position: nothing stops a run naming two
                          clusters the same, and a duplicate key would drop one
                          of them from the list silently. */}
                      {genReport.clusters.slice(0, 20).map((c, i) => (
                        <span key={i} className="hds-badge hds-badge--neutral" title={c.why || ""}>{c.name} · {c.size}</span>
                      ))}
                      {genReport.clusters.length > 20 && <span className="hds-table__muted">and {genReport.clusters.length - 20} more</span>}
                    </div>
                  </>
                ) : (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, color: "var(--text-muted)" }}>
                    <MlIcon name="alert-triangle" size={14} style={{ marginTop: 2 }} />
                    <span>
                      {genReport.grouping.error
                        ? `The grouping pass did not finish: ${genReport.grouping.error} Categories were proposed from the ungrouped list, so look at them more closely than usual.`
                        : genReport.grouping.why
                          ? `No grouping pass: ${genReport.grouping.why}`
                          : "The grouping pass produced no clusters, so categories were proposed from the ungrouped list."}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {error && <div className="bi-subempty" style={{ color: "var(--danger)" }}>{error}</div>}

        {/* What the last bulk run did. Approving writes to someone's live
            WordPress and any single row can be refused, so the failures are
            named with their reasons: "12 approved" alone would leave the user
            believing the other 3 exist as terms. */}
        {report && (
          <div className="bi-subempty" style={{ display: "flex", alignItems: "flex-start", gap: 8, color: report.failures.length ? "var(--danger)" : undefined }}>
            <MlIcon name={report.failures.length ? "alert-triangle" : "check"} size={14} />
            <div>
              <div>
                {report.ok} {report.past}
                {report.gone ? `, ${report.gone} no longer there` : ""}
                {report.failures.length ? `, ${report.failures.length} of ${report.attempted} could not be:` : "."}
              </div>
              {report.failures.length > 0 && (
                <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  {report.failures.slice(0, 8).map(f => <li key={f.id}><strong>{f.name}</strong>: {f.error}</li>)}
                  {report.failures.length > 8 && <li>and {report.failures.length - 8} more.</li>}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="hds-table-wrap">
          {!selectMode ? (
            <div className="ml-table-toolbar">
              <div className="hds-tabs" role="tablist">
                {SUGG_TABS.map(t => (
                  <button key={t.id} role="tab" className={["hds-tab", tab === t.id ? "hds-tab--active" : ""].filter(Boolean).join(" ")} onClick={() => setTab(t.id)}>
                    {t.label}<span className="hds-tab__count">{counts[t.id] || 0}</span>
                  </button>
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <div className="hds-tabs" role="tablist">
                {["all", "category", "tag"].map(k => (
                  <button key={k} role="tab" className={["hds-tab", kind === k ? "hds-tab--active" : ""].filter(Boolean).join(" ")} onClick={() => setKind(k)}>
                    {k === "all" ? "All" : k === "category" ? "Categories" : "Tags"}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="ml-table-toolbar ml-table-toolbar--bulk" style={{ flexWrap: "wrap", rowGap: 8 }}>
              <strong style={{ fontSize: "var(--text-sm)" }}>{selected.length} selected</strong>
              {/* Was leadingIcon="check-square", an MlIcon extra the DS does not
                  have: it rendered an empty svg, which is the blank gap that was
                  showing to the left of the label. */}
              <Button variant="secondary" size="sm" leadingIcon="check-circle" onClick={() => setSelected(view.map(r => r.id))}>Select all ({view.length})</Button>
              {selected.length > 0 && <Button variant="secondary" size="sm" onClick={() => setSelected([])}>Clear</Button>}
              {/* Named, not silently skipped: approving is only defined for a
                  draft, and a user who selected 20 rows across a filter should
                  know why the button says 14. */}
              {ineligible > 0 && (
                <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                  {ineligible} already decided, so {eligible.length} can be approved or rejected
                </span>
              )}
              <div style={{ flex: 1 }} />
              {bulkBusy
                ? <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{bulkBusy.gerund} {bulkBusy.done} / {bulkBusy.total}…</span>
                : bulkActions.map(act => (
                    <Button key={act.status} variant={act.variant} size="sm" leadingIcon={act.icon}
                      disabled={!eligible.length}
                      onClick={() => (act.danger ? setConfirmBulk(act) : runBulk(act))}>
                      {act.label}{eligible.length ? ` (${eligible.length})` : ""}
                    </Button>
                  ))}
            </div>
          )}

          <table className="hds-table">
            <thead><tr>
              {selectMode && <th className="ml-th-check"><label className="hds-check"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all suggestions in this view" /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></th>}
              <th>Name</th><th>Type</th><th>From</th><th>Confidence</th><th>Used</th><th>Why</th><th>Status</th><th style={{ width: 150 }}></th>
            </tr></thead>
            <tbody>
              {view.map((it) => (
                <tr key={it.id}>
                  {selectMode && <td className="ml-checkcell"><label className="hds-check"><input type="checkbox" checked={selected.includes(it.id)} onChange={() => toggleOne(it.id)} aria-label={`Select ${it.name}`} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label></td>}
                  <td className="hds-table__primary">{it.name}</td>
                  <td><span className="hds-badge hds-badge--neutral">{it.kind === "category" ? "Category" : "Tag"}</span></td>
                  {/* Before the decision, not after it: approving writes a real
                      term into WordPress, so what the term was built on has to
                      be readable while it can still be rejected. */}
                  <td>{fromChip(it)}</td>
                  <td className="hds-table__muted">{it.confidence != null ? it.confidence + "%" : "-"}</td>
                  <td>{it.used != null ? <span className="hds-badge hds-badge--neutral">{it.used}</span> : <span className="hds-table__muted">-</span>}</td>
                  <td className="hds-table__muted" title={it.rationale || ""} style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.rationale || it.description || "-"}</td>
                  <td>
                    {suggStatusBadge(eff(it))}
                    {/* An approved row is a claim about WordPress, and that
                        claim can go stale: a term deleted there leaves this row
                        still saying Approved. Only `false` is shown, never
                        `null`: null means the site could not be read, and
                        rendering that as "gone" would accuse WordPress of
                        losing terms whenever the site is briefly down.
                        Approving again is the fix and it now self-heals, so the
                        badge says what to do rather than only what is wrong. */}
                    {it.term_present === false && (
                      <span className="hds-badge hds-badge--danger" style={{ marginLeft: 6 }}
                        title="This term is not in WordPress any more. Approve it again to recreate it.">
                        Missing in WordPress
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="ml-rowactions">
                      {eff(it) === "draft" && <Button variant="primary" size="sm" disabled={busyId === it.id || !!bulkBusy} onClick={() => patch(it.id, "approved")}>Approve</Button>}
                      {eff(it) === "draft" && <Button variant="secondary" size="sm" disabled={busyId === it.id || !!bulkBusy} onClick={() => patch(it.id, "rejected")}>Reject</Button>}
                      {/* Recreating goes through the ordinary approve, which
                          now checks the stored id first and creates a fresh
                          term when it has gone. One path, so a recreate cannot
                          drift from an approve. */}
                      {it.term_present === false && (
                        <Button variant="primary" size="sm" disabled={busyId === it.id || !!bulkBusy}
                          onClick={() => patch(it.id, "approved")}>Recreate</Button>
                      )}
                      {(eff(it) === "approved" || eff(it) === "in_use") && <Button variant="secondary" size="sm" disabled={busyId === it.id || !!bulkBusy} onClick={() => patch(it.id, "archived")}>Archive</Button>}
                      {(eff(it) === "rejected" || eff(it) === "archived") && <Button variant="secondary" size="sm" disabled={busyId === it.id || !!bulkBusy} onClick={() => patch(it.id, "draft")}>Restore</Button>}
                      {/* No delete on a draft, matching the bulk bar. Reject
                          reaches the same place and keeps the record of having
                          decided; deleting discards it, and the next generation
                          run is then free to propose the same term again with
                          nothing to say it was already turned down. */}
                      {eff(it) !== "draft" && <IconButton icon="trash" label="Delete" size="sm" disabled={busyId === it.id || !!bulkBusy} onClick={() => remove(it.id)} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading ? <div className="bi-subempty">Loading…</div> : view.length === 0 && (
            <div style={{ padding: "8px 0" }}>
              {EmptyState
                ? <EmptyState icon="star" title={items.length ? "Nothing in this view" : "No suggestions yet"} description={items.length ? "Switch tabs or filters to see other suggestions." : "Generate AI category & tag suggestions from your content, approved keywords and research topics."} />
                : <div className="bi-subempty">{items.length ? "Nothing in this view." : "No suggestions yet, click Generate suggestions."}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Taxonomy Audit: scan posts, suggest which terms to APPLY, write back ----
  function chips(list, tone) {
    return (list || []).map((n, i) => <span key={i} className={"hds-badge hds-badge--" + tone} style={{ marginRight: 4, marginBottom: 2, display: "inline-block" }}>{n}</span>);
  }
  function TaxonomyAuditScreen({ site }) {
    const { Button, EmptyState } = DS();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const fullUrl = fullUrlOf(site);
    const [rows, setRows] = useState([]);
    const [scanned, setScanned] = useState(0);
    const [phase, setPhase] = useState("idle");  // idle | running | done
    const [loaded, setLoaded] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [bulk, setBulk] = useState(false);
    const [error, setError] = useState("");

    const loadLatest = useCallback(async () => {
      if (siteId == null) { setLoaded(true); return; }
      try {
        const d = await api(`/taxonomy/audit?site_id=${siteId}`);
        setRows(Array.isArray(d && d.suggestions) ? d.suggestions : []);
        setScanned((d && d.scanned) || 0);
        if (d && Array.isArray(d.suggestions) && d.suggestions.length) setPhase("done");
      } catch { /* no snapshot yet */ } finally { setLoaded(true); }
    }, [siteId]);
    useEffect(() => { loadLatest(); }, [loadLatest]);

    const run = async () => {
      if (siteId == null || !fullUrl) return;
      setPhase("running"); setError("");
      try {
        const d = await api(`/taxonomy/audit`, { method: "POST", body: JSON.stringify({ site_id: siteId, siteUrl: fullUrl }) });
        setRows(Array.isArray(d && d.suggestions) ? d.suggestions : []);
        setScanned((d && d.scanned) || 0);
        setPhase("done");
      } catch (e) { setError(e.message || "Audit failed"); setPhase(rows.length ? "done" : "idle"); }
    };
    const applyOne = async (row) => {
      setBusyId(row.post_id); setError("");
      try {
        await api(`/taxonomy/audit/apply`, { method: "POST", body: JSON.stringify({ site_id: siteId, siteUrl: fullUrl, applications: [{ post_id: row.post_id, add_categories: row.add_categories, add_tags: row.add_tags }] }) });
        setRows(rs => rs.filter(r => r.post_id !== row.post_id));
      } catch (e) { setError(e.message || "Apply failed"); } finally { setBusyId(null); }
    };
    const applyAll = async () => {
      if (!rows.length) return;
      setBulk(true); setError("");
      try {
        const applications = rows.map(r => ({ post_id: r.post_id, add_categories: r.add_categories, add_tags: r.add_tags }));
        const d = await api(`/taxonomy/audit/apply`, { method: "POST", body: JSON.stringify({ site_id: siteId, siteUrl: fullUrl, applications }) });
        const okIds = new Set((d.results || []).filter(x => x.ok).map(x => x.post_id));
        setRows(rs => rs.filter(r => !okIds.has(r.post_id)));
      } catch (e) { setError(e.message || "Apply failed"); } finally { setBulk(false); }
    };

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">Taxonomy Audit</h1>
            <p className="ml-pagehead__sub">Scan posts and apply missing categories &amp; tags{site ? " on " + site.name : ""}. Suggestions use your existing terms and approved suggestions.</p>
          </div>
          <div className="ml-pagehead__actions">
            {rows.length > 0 && <Button variant="secondary" leadingIcon="check" disabled={bulk || phase === "running"} onClick={applyAll}>{bulk ? "Applying…" : "Apply all"}</Button>}
            <Button variant="primary" leadingIcon={phase === "done" ? "refresh" : "search"} disabled={phase === "running" || siteId == null} onClick={run}>
              {phase === "running" ? "Auditing…" : phase === "done" ? "Re-run audit" : "Run audit"}
            </Button>
          </div>
        </div>

        {error && <div className="bi-subempty" style={{ color: "var(--danger)" }}>{error}</div>}

        {phase !== "running" && rows.length > 0 && (
          <div className="hds-table-wrap">
            <div className="ml-table-toolbar">
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{rows.length} post{rows.length === 1 ? "" : "s"} with suggestions · {scanned} scanned</span>
            </div>
            <table className="hds-table">
              <thead><tr><th>Post</th><th>Add categories</th><th>Add tags</th><th style={{ width: 110 }}></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.post_id}>
                    <td className="hds-table__primary">{r.link ? <a href={r.link} target="_blank" rel="noreferrer">{r.title || "(untitled)"}</a> : (r.title || "(untitled)")}</td>
                    <td>{r.add_categories && r.add_categories.length ? chips(r.add_categories, "info") : <span className="hds-table__muted">-</span>}</td>
                    <td>{r.add_tags && r.add_tags.length ? chips(r.add_tags, "neutral") : <span className="hds-table__muted">-</span>}</td>
                    <td><Button variant="primary" size="sm" disabled={busyId === r.post_id || bulk} onClick={() => applyOne(r)}>{busyId === r.post_id ? "Applying…" : "Apply"}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {phase === "running" && <div className="bi-subempty"><span className="ml-spinner ml-spinner--sm" style={{ marginRight: 8 }} />Scanning posts and matching terms…</div>}
        {phase !== "running" && loaded && rows.length === 0 && (
          <div style={{ padding: "8px 0" }}>
            {EmptyState
              ? <EmptyState icon="search" title={scanned ? "Nothing to apply" : "Run a taxonomy audit"} description={scanned ? "Every scanned post already has the relevant categories and tags." : "Scan your published posts and get AI suggestions for which existing or approved categories & tags to add."} />
              : <div className="bi-subempty">{scanned ? "Nothing to apply." : "Run a taxonomy audit to get suggestions."}</div>}
          </div>
        )}
      </div>
    );
  }

  // navPosition is exported for its tests, not for a screen: it is the one part
  // of the drawer's stepping with edge cases and the only part testable without
  // a browser.
  window.MlContent = { PagesScreen, CategoriesScreen, TagsScreen, TaxonomySuggestionsScreen, TaxonomyAuditScreen, navPosition };
})();
